/**
 * Thin client for the server-api auth/link endpoints (IMPL-03). Every call attaches the current
 * Firebase ID token as `Bearer`; the server derives `userId` from it (never from the body). The
 * shapes mirror server-api's AuthService responses.
 */
import type {
  AgendaSettings,
  ToolbarConfig,
  EngineConfig,
  EngineCatalogEntry,
  CalendarPrefs,
  Integration as HubIntegration,
  SubscriptionDigestConfig,
  SubscriptionFeedbackEntry,
  SubscriptionFeedbackVerdict,
  SubscriptionIssue,
  SubscriptionIssueSummary,
  SubscriptionRule,
  SubscriptionRuleMatch,
  SubscriptionRuleAction,
  GranolaConfig,
  SlackConfig,
  SlackWorkspaceCfg,
  GeminiConfig,
  MovieEnricherConfig,
  RestaurantVerifierConfig,
  InstagramShareConfig,
  PodcastConfig,
  PodcastRunView,
  DetailLevel,
  OutputTargetKind,
  VoiceConfig,
  VoiceResources,
  BillPayConfig,
  PaymentMethodMeta,
  BankAccountMeta,
  BillPayDraft,
  BillRecord,
  Job,
  WatchSource,
  WatchPredicate,
  WatchSignalTemplate,
  BookmarkMeta,
  FinancialConfig,
  FinancialTransaction,
  FinancialAccount,
  RuleCondition,
  RecurringStream,
  FinancialAlert,
  NetWorth,
  CategorySummary,
  OwnerSummary,
  CategoryGroup,
  SpendWindow,
  TrendPoint,
  TodoistProject,
  Signal,
  SignalMetrics,
  CreateActionKind,
  LinkedArtifact,
  TrustRule,
  TrustRuleTarget,
  RoutingRule,
  AutonomyEvent,
  AutonomyScorecardDay,
  Utility,
  GmailCleanupConfig,
  UtilityRun,
  DecisionRecord,
  DecisionCorrectionReason,
  DecisionRule,
  DecisionRuleKind,
  DecisionQuery,
  DecisionDetailDTO,
  DecisionStatus,
  DecisionLedgerConfig,
  ManualDecisionInput,
  CapabilityManifest,
  UserContact,
  WritingStyleProfile,
  WorkingHours,
  ParkedIngest,
  KindUsefulness,
  GuidanceEntry,
  DeliveryRecord,
  ProducerId,
  RedditConfig,
  RedditPost,
  MenuLayout,
  ClaveroConfig,
  ShoppingSummary,
  ShoppingCartSnapshot,
  LapoOrderIntent,
  ConfirmSurface,
  ProductCandidate,
  ProductRead,
  AmazonOrder,
  AmazonTxnMatch,
  AmazonBucketRollup,
  BillerDossier,
  BillerOverlay,
  HealthSnapshot,
  HealthEvent,
  RawLogEntry,
  Rule,
  Grant,
  GrantActionKind,
} from '@lapo/core';

/** SYSTEM-HEALTH — a curated health event as the client receives it (core's HealthEvent shape). */
export type HealthEventDTO = HealthEvent;
export type { HealthSnapshot, RawLogEntry };

/** A live Reddit Monitor fetch: matched posts (newest first) + per-channel scan stats + an optional
 *  setup error (`not-configured` | `no-channels` | `no-key`) the view surfaces as a call to action. */
export interface RedditResults {
  posts: RedditPost[];
  channels: { subreddit: string; matched: RedditPost[]; scanned: number; error?: string }[];
  error?: string;
}

/** One signal card (GET /signals). Mirrors the core `Signal`. */
export type SignalDTO = Signal;
/** The result of acting on a signal (POST /signals/:id/act) — the created artifact ref, if any. */
export interface SignalActResultDTO {
  signal: SignalDTO;
  created?: { kind: 'task' | 'note' | 'draft'; ref: string };
  /** Set when the write needs an escalated Google scope the account hasn't granted (spec A10). */
  relinkRequired?: string;
}

/** HELM P1-8 — the morning brief for a day (GET /me/attention/brief/:dateKey). */
export interface MorningBriefDTO {
  dateKey: string;
  items: Array<{ subjectKey: string; section: 'decision' | 'handled' | 'left-alone'; title: string; detail: string }>;
  /** The ledger deliveryId the reaction thumbs patch; absent until the brief has been sent. */
  deliveryId?: string;
}

/** The source email behind a draft-reply card (GET /signals/:id/source-email), for the Review-draft dialog. */
export interface SignalSourceEmailDTO {
  from: string;
  fromAddress: string;
  subject: string;
  /** ISO timestamp of the source message. */
  date: string;
  /** Plain-text body (best-effort decode). */
  text: string;
  /** Gmail's short preview snippet (fallback when `text` is empty). */
  snippet: string;
}

/** HONE — the result of spawning an artifact from a signal (POST /signals/:id/create). */
export interface SignalCreateArtifactResultDTO {
  signal: SignalDTO;
  artifact?: LinkedArtifact;
  /** Set when the create needs an escalated Google scope the account hasn't granted (draft). */
  relinkRequired?: string;
}

/** An accreted per-category disposition policy entry (ACT-06 rule table). */
/** Outcome of an Instagram add-by-URL import (mirrors the share endpoint's AcceptResult). */
export interface InstagramImportResult {
  status: 'accepted' | 'duplicate' | 'unconfigured';
  importId?: string;
  blockId?: string;
}

/** Options for an in-editor Instagram import (slash command / paste): heading level from outline depth. */
export interface InstagramImportOptions {
  /** Heading level (1–6) = the caret's outline depth. Server defaults to 1 (top-level → H1). */
  level?: number;
  /** 'client' when the editor already dropped the placeholder at the caret (skip the server one). */
  place?: 'server' | 'client';
  /** Client-generated import id so the placeholder's `gram-<id>` matches the eventual enriched section. */
  importId?: string;
}

export interface DispositionPolicyEntryDTO {
  category: string;
  policy: 'confirm' | 'gated' | 'auto' | 'escalate' | 'spam' | 'automated' | 'spam-pending';
  updatedAt: number;
  /** CULL — for `spam-pending`: when the quiet countdown commits this to a full spam rule. */
  commitAfter?: number;
}

/** MARROW — one durable assistant memory (AI Coaching → Memory manager). */
export interface AssistantMemoryDTO {
  memoryId: string;
  kind: 'preference' | 'fact' | 'instruction' | 'project';
  text: string;
  /** `chat` = distilled from conversation; `manual` = typed in the manager; `reflection`/`planner` = machine (HELM P3-1). */
  source: 'chat' | 'manual' | 'reflection' | 'planner';
  createdAt: number;
  updatedAt: number;
  lastSeenAt: number;
  disabled?: boolean;
  /** HELM P3-1/P3-6 — a pinned `preference|fact|project` record renders in the USER.md pane. */
  pinned?: boolean;
  /** HELM P3-1 — `YYYY-MM-DD` this became the standing line (USER.md dated lines); falls back to createdAt. */
  since?: string;
  /** HELM P3-1 — the `memoryId` this record supersedes (kept + marked, never deleted — DL-17). */
  supersedes?: string;
}

/** HELM P3-6 — a RULES.md line (the core `Rule`): a dated, reversible standing correction. */
export type RuleDTO = Rule;

/** HELM P3-4/P3-6 — the provenance chain for one ref (the "why?" affordance). `found:false` is not an error. */
export interface ExplainResultDTO {
  ref: string;
  kind: 'subject' | 'rule' | 'memory' | 'unknown';
  found: boolean;
  text: string;
}

/** An auto-graduation proposal (ACT-06 / spec A16). */
export interface DispositionProposalDTO {
  category: string;
  disposition: string;
  approved: number;
  reason: string;
}

/** AXIS trust rule (X14 ledger row) — mirrors the core `TrustRule`. */
export type TrustRuleDTO = TrustRule;
/** AXIS per-rule downstream-target override (X27). */
export type TrustRuleTargetDTO = TrustRuleTarget;
/** AXIS routing rule (X26 ledger row) — mirrors the core `RoutingRule`. */
export type RoutingRuleDTO = RoutingRule;
/** AXIS autonomy-log event (recap rows — X4). */
export type AutonomyEventDTO = AutonomyEvent;
/** AXIS composite scorecard day (X19). */
export type AutonomyScorecardDayDTO = AutonomyScorecardDay;
/** HELM P4-3 — a live standing grant (the Autonomy module's grants list) — mirrors the core `Grant`. */
export type GrantDTO = Grant;
/** The scope the owner picks on the graduation accept card (`ask` = no grant, so it is excluded). */
export type GrantScopeChoice = 'once' | 'subject' | 'always';

/** A per-key learning aggregate (the inspectable "what Lapo has learned" view — spec A15). */
export interface DispositionLearnedDTO {
  key: string;
  disposition: string;
  approved: number;
  edited: number;
  rejected: number;
  overridden: number;
  total: number;
  lastAt: number;
}

/** The dashboard payload (GET /finances/summary). All money is integer cents. */
export interface FinancialSummaryDTO {
  netWorth: NetWorth;
  accounts: FinancialAccount[];
  categories: CategorySummary[];
  buckets: CategorySummary[];
  spendWindow: SpendWindow;
  ownerSplit: OwnerSummary[];
  trend: TrendPoint[];
  recurring: RecurringStream[];
  alerts: FinancialAlert[];
}

// ── Fitness (Tesseta) DTOs — mirror the server's integrations/tesseta types over the wire ───────────
export interface BodyCompPointDTO {
  id: string;
  metric: string;
  value: number;
  sampleTime: string;
  recordingMethod?: string;
  sourcePlatform?: string;
}
export interface DexaScanDTO {
  id: string;
  measuredOn?: string;
  totalBodyFatPercent?: number;
  totalMassLb?: number;
  fatTissueLb?: number;
  leanTissueLb?: number;
  sourceFacility?: string;
  updatedAt?: string;
}
export interface WorkoutComplianceProgramDTO {
  id: string;
  title?: string;
  status?: string;
  scheduledSessions: number;
  completedSessions: number;
  compliancePct: number;
}
export interface FitnessSummaryDTO {
  generatedAt: number;
  user?: { displayName?: string; heightCm?: number };
  latestWeight?: { value: number; at: string };
  latestBodyFatPercent?: { value: number; at: string };
  latestLeanMass?: { value: number; at: string };
  weightChange30d?: number;
  latestDexa?: DexaScanDTO;
  overallCompliancePct: number;
  currentStreakDays: number;
  totalCompletedWorkouts: number;
  programs: WorkoutComplianceProgramDTO[];
}
export interface TessetaConfigDTO {
  enabled: boolean;
  assistantReads?: boolean;
  weightUnit?: 'lb' | 'kg';
  lastSyncAt?: number;
  lastError?: string;
}
export interface FitnessSummaryResponse {
  summary: FitnessSummaryDTO | null;
  config: TessetaConfigDTO | null;
}
export interface BodyCompositionSeriesDTO {
  points: BodyCompPointDTO[];
  dexa: DexaScanDTO[];
}
export interface WorkoutComplianceViewDTO {
  programs: WorkoutComplianceProgramDTO[];
  overallCompliancePct: number;
  currentStreakDays: number;
  totalCompletedWorkouts: number;
  calendar: Array<{ date: string; count: number; isDeload: boolean }>;
}

/** A vendor rename rule (exact merchant name or a regex pattern). */
export interface RenameRuleDTO {
  id: string;
  pattern: string;
  isRegex: boolean;
  displayName: string;
}

/** The user's automation rules (GET /finances/rules): category rules + vendor renames. */
export interface FinancialRulesDTO {
  categoryRules: Array<{ ruleId: string; normalizedMerchant?: string; category: string; createdAt: number; conditions?: RuleCondition[]; priority?: number }>;
  renameRules: RenameRuleDTO[];
}

/** One flagged rule from the LLM audit (POST /finances/rules/audit). */
export interface RuleAuditFindingDTO {
  target: 'rename' | 'category';
  id: string;
  reason: string;
}
/** A proposal to replace several exact rename rules with one regex. */
export interface RegexSuggestionDTO {
  pattern: string;
  displayName: string;
  replaces: string[];
  reason: string;
}
/** The LLM rule-audit result. */
export interface RuleAuditDTO {
  findings: RuleAuditFindingDTO[];
  suggestions: RegexSuggestionDTO[];
}

/** Result of a Monarch CSV import. */
export interface MonarchImportResultDTO {
  parsed: number;
  matched: number;
  backApplied: number;
  rulesCreated: number;
  categoriesSeeded: number;
}

/** A connected Plaid institution (GET /me/financial/items). */
export interface FinancialItemDTO {
  itemId: string;
  institutionName: string;
  status: string;
  lastSyncAt: number;
}

/** Filter for the transaction feed (GET /finances/transactions). */
export interface TransactionQuery {
  accountId?: string;
  category?: string;
  /** Exact normalized-merchant match (a recurring stream's underlying charges). */
  merchant?: string;
  reviewStatus?: string;
  owner?: string;
  from?: string;
  to?: string;
  limit?: number;
}

/** One of the user's Google calendars with its resolved watched state (Automations → Watched calendars). */
export interface UserCalendarDTO {
  accountId: string;
  accountEmail?: string;
  calendarId: string;
  summary?: string;
  primary?: boolean;
  enabled: boolean;
}

export type ProviderId = 'google' | 'apple';
export type Integration = 'gmail' | 'calendar' | 'drive' | 'people' | 'todoist' | 'easypost' | 'amazon' | 'claude' | 'granola' | 'financial' | 'tesseta';

/** What the OAuth consent screen renders: the requesting app + the scopes it's asking for. */
export interface OAuthConsentInfo {
  clientName: string;
  scopes: { scope: string; title: string; description: string }[];
}

/** The authorization request the consent screen approves (Authorization Code + PKCE). */
export interface OAuthAuthorizeInput {
  clientId: string;
  redirectUri: string;
  scope: string;
  state?: string;
  codeChallenge: string;
  codeChallengeMethod: string;
}

/** A third-party app the user has authorized (Settings → Authorized apps). */
export interface AuthorizedAppDTO {
  grantId: string;
  clientId: string;
  clientName: string;
  scopes: string[];
  createdAt: number;
  lastUsedAt?: number;
}

/** A postal address in EasyPost's shape. */
export interface ShipAddressDTO {
  name: string;
  company?: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone?: string;
  email?: string;
}

/** Parcel dimensions (inches) and weight (ounces). */
export interface ParcelDTO {
  length: number;
  width: number;
  height: number;
  weight: number;
}

/** A shipment the LLM extracted from a prompt, before it is bought. */
export interface ShipmentDraftDTO {
  to: ShipAddressDTO;
  from: ShipAddressDTO;
  parcel: ParcelDTO;
  reference?: string;
}

/** A purchasable carrier rate. */
export interface RateDTO {
  id: string;
  carrier: string;
  service: string;
  rate: string;
  currency: string;
  deliveryDays?: number | null;
}

/** Delivery status, normalized from EasyPost tracker statuses. */
export type ShipmentStatusDTO =
  | 'unknown'
  | 'pre_transit'
  | 'in_transit'
  | 'out_for_delivery'
  | 'delivered'
  | 'available_for_pickup'
  | 'return_to_sender'
  | 'failure'
  | 'cancelled';

/** Result of drafting + pricing a shipment: the EasyPost id and the rates to choose from. */
export interface DraftResultDTO {
  draft: ShipmentDraftDTO;
  easypostId: string;
  rates: RateDTO[];
}

/** Which action the assistant took for one natural-language request. */
export type AssistantToolDTO = 'add_task' | 'add_note' | 'create_shipping_label' | 'track_package' | 'place_call' | 'pay_bill' | 'none';

/** Result of one ✦ assistant turn: the action taken plus any tool-specific payload to render. */
export interface AssistantResultDTO {
  tool: AssistantToolDTO;
  /** short first-person confirmation / clarification to show in the chat */
  message: string;
  /** false only for `none` — nothing was done */
  ok: boolean;
  task?: { taskId: string; content: string; accountId: string };
  note?: { noteId: string; title: string };
  shipping?: DraftResultDTO;
  tracked?: { trackedId: string; trackingCode: string; carrier: string; status: string };
  /** a placed outbound voice call (DIAL) — poll {@link Api.voiceCallStatus} until it goes terminal. */
  call?: { callId: string; state: string };
  /** REMIT-CHAT — a bill read from a shared image, drafted for the user to CONFIRM before paying. */
  billDraft?: BillPayDraft;
}

/**
 * DOCKET — one payment run as served by `GET /me/bills/{id}/runs`. Mirrors the worker's
 * `BillPayRun` (server-api `payments/stores.ts`); steps are sanitized at write time (refs +
 * outcome markers, never card/field values). `errorStack` display is gated client-side on
 * `BillPayConfig.showDiagnostics`.
 */
/** SENTRY — a watch rule as served by `/me/watches` (the stored rule + its English-rendered predicate). */
export interface Watch {
  id: string;
  userId: string;
  label: string;
  source: WatchSource;
  enabled: boolean;
  predicate: WatchPredicate;
  onMatch: WatchSignalTemplate;
  createdBy: 'assistant' | 'owner';
  createdAt: number;
  quarantinedAt?: number;
  stats: { fireCount: number; firesToday: number; lastFiredAt?: number; sandboxErrors: number };
  /** Plain-English rendering of the predicate (server-computed). */
  english: string;
}

/** SENTRY — the body for `POST /me/watches` (form-builder authoring). */
export interface WatchCreateInput {
  label: string;
  source: WatchSource;
  predicate: WatchPredicate;
  onMatch: WatchSignalTemplate;
}

export interface BillPayRunDTO {
  runId: string;
  billId: string;
  status: 'queued' | 'running' | 'paid' | 'failed';
  createdAt: number;
  updatedAt: number;
  steps: Array<{ action: string; ref?: string; outcome: string; at: number }>;
  bytesUsed: number;
  playbookUsed?: boolean;
  error?: string;
  errorStack?: string;
  // TEMPER — the attempt ledger's taxonomy + lesson (spec §3.4).
  bucket?: 'paid' | 'retryable' | 'needs-you' | 'stalled';
  abortSignature?: string;
  stage?: string;
  reflection?: { note?: string; dossierApplied?: boolean };
}

/** Live state of one placed voice call (DIAL) — the palette's status-chip poll target. */
export interface VoiceCallStatusDTO {
  callId: string;
  state: 'dialing' | 'in-progress' | 'completed' | 'failed' | 'no-answer' | 'voicemail';
  targetName: string;
  endedReason?: string;
  outcome?: { succeeded: boolean; summary: string };
  /** The same owner-facing one-liner the server appends to the thread / pushes to Telegram. */
  outcomeLine?: string;
}

/** One persisted ✦ conversation turn, replayed to rehydrate the palette on open. */
export interface AssistantTurnDTO {
  role: 'user' | 'assistant';
  text: string;
  tool?: AssistantToolDTO;
  at: number;
}

/** A bought label + its live tracking state (the "previous labels" row). */
export interface ShipmentDTO {
  shipmentId: string;
  easypostId: string;
  to: ShipAddressDTO;
  from: ShipAddressDTO;
  parcel: ParcelDTO;
  carrier: string;
  service: string;
  rate: string;
  currency: string;
  trackingCode: string;
  labelUrl: string;
  status: ShipmentStatusDTO;
  statusDetail?: string;
  estDeliveryDate?: string;
  /** User title/nickname for the label; shown in place of the carrier when set. */
  label?: string;
  /** User description/note about the shipment. */
  note?: string;
  /** Set when archived (hidden from the default Previous-labels list); unset = active. */
  archivedAt?: number;
  createdAt: number;
  updatedAt: number;
}

/** An arbitrary package tracked by tracking number (EasyPost Tracker), not a bought label. */
export interface TrackedPackageDTO {
  trackedId: string;
  trackerId: string;
  trackingCode: string;
  carrier: string;
  status: ShipmentStatusDTO;
  statusDetail?: string;
  estDeliveryDate?: string;
  signedBy?: string;
  signatureRequired?: boolean;
  label?: string;
  /** What was shipped, read from the source email when auto-captured. */
  item?: string;
  /** Set when archived (hidden from the default Shipments list); unset = active. */
  archivedAt?: number;
  createdAt: number;
  updatedAt: number;
}

export interface ContactDTO {
  contactId: string;
  displayName?: string;
  emails: string[];
  phones?: string[];
  org?: string;
  title?: string;
  photoUrl?: string;
}

/** ORBIT — the passive relationship profile served to the contact page (D-14). Mirrors core's RelationshipProfile. */
export interface RelationshipProfileDTO {
  entityKey: string;
  displayName: string;
  primaryEmail?: string;
  category: 'work' | 'personal';
  lastTouchAt?: string;
  lastTouchChannel?: 'email' | 'meeting';
  lastInboundAt?: string;
  lastOutboundAt?: string;
  interactionCount: number;
  cadenceMedianDays?: number;
  overdue: boolean;
  overdueByDays?: number;
  owedReply: boolean;
  recentCommsSummary?: string;
  externalNews?: Array<{ headline: string; url: string; source?: string; publishedAt?: string }>;
  commitments?: Array<{ text: string; direction: 'i-owe' | 'they-owe'; done: boolean; sourceRef?: string }>;
  introducer?: { name?: string; email?: string; confirmed: boolean; dismissed?: boolean };
}

// --- LOOM (initiative×quarter timeline matrix; docs/plans/initiatives-timeline-loom.md) ---
export type LoomAggressiveness = 'conservative' | 'normal' | 'eager';
export type LoomPlacementState = 'placed' | 'suggested';
export type LoomPlacementOrigin = 'tag' | 'grid' | 'engine';
export type LoomItemKind = 'jot' | 'bookmark' | 'image';
export type LoomPlacementItemKind = 'block' | 'note' | 'event' | 'entity' | 'ref' | 'jot' | 'bookmark' | 'image';
export interface LoomExemplarDTO {
  summary: string;
  polarity: 'positive' | 'negative';
  at: number;
}
/** TRELLIS: kind of node in the initiative hierarchy. */
export type LoomInitiativeKind = 'area' | 'initiative' | 'cycle';
/** TRELLIS: the lifecycle status of an initiative (drives the status chips + lanes). */
export type LoomInitiativeStatus = 'active' | 'stale' | 'blocked' | 'done' | 'archived';
/** TRELLIS: a settable status — the subset an owner can assign via the UI (never 'stale'/'archived'). */
export type LoomSettableStatus = 'active' | 'blocked' | 'done';
export interface LoomMilestoneDTO {
  label: string;
  due: number | null;
}
/** TRELLIS: the owner- or engine-authored brief that summarizes an initiative's state. */
export interface LoomBriefDTO {
  summary: string;
  openQuestions: string[];
  nextMilestone: LoomMilestoneDTO | null;
  stakeholders: string[];
  updatedBy: string;
  updatedAt: number;
}
/** TRELLIS: recurrence spec for a cadence-driven initiative. */
export interface LoomCadenceDTO {
  every: string;
  anchor: number | null;
  leadDays: number | null;
  calendarEventUid: string | null;
  customDays: number | null;
}
/** TRELLIS: how a recurring "cycle" initiative mints each new occurrence. */
export interface LoomCycleSpecDTO {
  instructions: string;
  templateNoteId: string | null;
  destination: string | null;
}
export interface LoomInitiativeDTO {
  id: string;
  userId: string;
  name: string;
  description: string;
  color: string;
  sortOrder: number;
  archived: boolean;
  engine: { enabled: boolean; aggressiveness: LoomAggressiveness };
  exemplars: LoomExemplarDTO[];
  stats: { autoPlaced: number; autoRemoved: number };
  createdAt: number;
  updatedAt: number;
  // --- TRELLIS fields (all optional; absent on legacy board payloads) ---
  parentId?: string | null;
  path?: string[];
  kind?: LoomInitiativeKind;
  status?: LoomInitiativeStatus;
  brief?: LoomBriefDTO | null;
  lastActivityAt?: number;
  cadence?: LoomCadenceDTO | null;
  cycleSpec?: LoomCycleSpecDTO | null;
}
/** TRELLIS: detail payload from GET /loom/initiatives/:id. */
export interface LoomInitiativeDetailDTO {
  id: string;
  name: string;
  description: string;
  status: LoomInitiativeStatus;
  kind: LoomInitiativeKind;
  parentName: string | null;
  recurring: boolean;
  brief: Pick<LoomBriefDTO, 'summary' | 'openQuestions' | 'nextMilestone'> | null;
  children: Array<{ name: string; status: LoomInitiativeStatus }>;
  /**
   * `itemKey` is optional: the server currently returns only `{ snippet, at }`, but when it is
   * present the detail panel deep-links reference items (email/Slack) to their source.
   */
  recentItems: Array<{ snippet: string; at: number; itemKey?: string }>;
}
/** TRELLIS: body for PUT /loom/initiatives/:id/brief (all fields optional patches). */
export interface LoomBriefPatch {
  summary?: string;
  openQuestions?: string[];
  stakeholders?: string[];
  nextMilestone?: LoomMilestoneDTO | null;
}
/** TRELLIS: a pending engine proposal awaiting owner accept/reject. */
export interface LoomProposalDTO {
  id: string;
  kind: string;
  title: string;
  rationale: string;
  evidence: Array<{ itemKey: string; snippet: string; source: string }>;
  payload: unknown;
  state: string;
  createdAt: number;
  ttlDays: number;
}
export interface LoomPlacementDTO {
  itemKey: string;
  userId: string;
  initiativeId: string;
  quarter: string;
  quarterOverride: string | null;
  state: LoomPlacementState;
  origin: LoomPlacementOrigin;
  confidence: number | null;
  evidence: string | null;
  snippet: string;
  thumbUri: string | null;
  kind: LoomPlacementItemKind;
  noteId: string | null;
  blockId: string | null;
  naturalDate: number;
  createdAt: number;
  updatedAt: number;
}
export interface LoomThreadDTO {
  id: string;
  userId: string;
  initiativeId: string;
  name: string;
  itemKeys: string[];
  createdAt: number;
  updatedAt: number;
}
export interface LoomLinkDTO {
  id: string;
  userId: string;
  aKey: string;
  bKey: string;
  createdAt: number;
}
export interface LoomItemDTO {
  id: string;
  userId: string;
  kind: LoomItemKind;
  text?: string;
  url?: string;
  favicon?: string;
  fileUri?: string;
  createdAt: number;
}
export interface LoomBoardQuarterDTO {
  key: string;
  label: string;
  isCurrent: boolean;
}
export interface LoomBoardDTO {
  initiatives: LoomInitiativeDTO[];
  placements: LoomPlacementDTO[];
  threads: LoomThreadDTO[];
  links: LoomLinkDTO[];
  quarters: LoomBoardQuarterDTO[];
  currentQuarter: string;
}
export interface LoomPickResultDTO {
  itemKey: string;
  kind: LoomPlacementItemKind;
  title: string;
  snippet: string;
  noteId: string | null;
  blockId: string | null;
  naturalDate: number;
  thumbUri: string | null;
}
export interface LoomInitiativePatch {
  name?: string;
  description?: string;
  color?: string;
  archived?: boolean;
  engine?: Partial<{ enabled: boolean; aggressiveness: LoomAggressiveness }>;
  // --- TRELLIS ---
  status?: LoomSettableStatus;
  parentId?: string | null;
  kind?: LoomInitiativeKind;
  cadence?: LoomCadenceDTO | null;
  cycleSpec?: LoomCycleSpecDTO | null;
}
export interface LoomPlacementUpsert {
  itemKey: string;
  initiativeId: string;
  origin?: LoomPlacementOrigin;
  quarterOverride?: string | null;
  naturalDate?: number;
  snippet?: string;
  kind?: LoomPlacementItemKind;
  noteId?: string | null;
  blockId?: string | null;
  thumbUri?: string | null;
  state?: LoomPlacementState;
}
export interface LoomApi {
  board(opts?: { all?: boolean; from?: string; to?: string }): Promise<LoomBoardDTO>;
  pick(q: string, limit?: number): Promise<LoomPickResultDTO[]>;
  createInitiative(input: { name: string; description?: string; color?: string; aggressiveness?: LoomAggressiveness; enabled?: boolean }): Promise<LoomInitiativeDTO>;
  updateInitiative(id: string, patch: LoomInitiativePatch): Promise<LoomInitiativeDTO>;
  deleteInitiative(id: string): Promise<void>;
  reorderInitiatives(orderedIds: string[]): Promise<void>;
  upsertPlacement(input: LoomPlacementUpsert): Promise<LoomPlacementDTO>;
  removePlacement(itemKey: string): Promise<LoomPlacementDTO | null>;
  createItem(input: { kind: LoomItemKind; text?: string; url?: string; fileUri?: string; initiativeId?: string; quarter?: string | null }): Promise<{ item: LoomItemDTO; placement?: LoomPlacementDTO }>;
  createThread(input: { initiativeId: string; name: string; itemKeys?: string[] }): Promise<LoomThreadDTO>;
  updateThread(id: string, patch: { name?: string; addItemKey?: string; removeItemKey?: string }): Promise<LoomThreadDTO>;
  deleteThread(id: string): Promise<void>;
  createLink(aKey: string, bKey: string): Promise<LoomLinkDTO>;
  deleteLink(id: string): Promise<void>;
  classify(noteId?: string): Promise<{ placed: number; suggested: number }>;
  // --- TRELLIS ---
  initiativeDetail(idOrName: string): Promise<LoomInitiativeDetailDTO>;
  patchInitiative(id: string, patch: LoomInitiativePatch): Promise<LoomInitiativeDTO>;
  putBrief(id: string, brief: LoomBriefPatch): Promise<LoomInitiativeDTO>;
  draftCycle(id: string): Promise<{ opened: number; drafted: number }>;
  proposals(state?: string): Promise<LoomProposalDTO[]>;
  acceptProposal(id: string): Promise<{ ok: boolean; initiativeId?: string; initiativeName?: string }>;
  rejectProposal(id: string): Promise<{ rejected: boolean }>;
}

export interface ContactSuggestionDTO {
  suggestionId: string;
  entityName: string;
  contactName: string;
  score: number;
}

/** A blob stored via POST /files — the stable `lapo://` reference plus its metadata. */
export interface UploadedBlobDTO {
  uri: string;
  path: string;
  bucketKind: 'images' | 'files';
  contentType: string;
  filename: string;
  size: number;
  kind: 'image' | 'pdf' | 'file';
}

/** A short-lived signed read URL for a stored blob (GET /files/sign). */
export interface SignedUrlDTO {
  url: string;
  expiresAt: number;
}

export type HeadshotModelTierDTO = 'flash' | 'pro';

/** Request to generate a synthetic headshot for a contact from uploaded reference photos. */
export interface GenerateHeadshotInput {
  contactId: string;
  /** `lapo://` references to the uploaded reference photos. */
  references: string[];
  style: string;
  model?: HeadshotModelTierDTO;
}

/** A generated, not-yet-approved headshot draft. */
export interface GenerateHeadshotResult {
  draftId: string;
  contactId: string;
  /** `lapo://` reference the client signs to render the preview. */
  previewUri: string;
  style: string;
  model: HeadshotModelTierDTO;
}

/** Outcome of approving a headshot draft — the photo now on the contact + refreshed integration. */
export interface ApproveHeadshotResult {
  contactId: string;
  photoUrl?: string;
  integration: HubIntegration | null;
}

/** A Todoist Today-panel task, colors pre-resolved to hex server-side. */
export interface TodoistTaskDTO {
  taskId: string;
  accountId: string;
  content: string;
  priority: number; // 1 (normal) .. 4 (urgent)
  priorityColor: string | null; // hex, or null for normal priority
  due: { date: string; datetime: string | null; hasTime: boolean; recurring: boolean } | null;
  overdue: boolean;
  labels: Array<{ name: string; color: string }>;
  project: { id: string; name: string; color: string } | null;
}

export interface TodoistTodayDTO {
  overdue: TodoistTaskDTO[];
  today: TodoistTaskDTO[];
  /** The stored token is revoked/invalid — the panel prompts a one-tap reconnect instead of a list. */
  needsReconnect?: boolean;
  /** A non-auth Todoist failure (transient) — shown as a readable note; the list is empty. */
  error?: string;
}

/** One event in the agenda sidebar's day view, tagged with the calendar account it came from. */
export interface AgendaEventDTO {
  id: string;
  accountId: string;
  /** Email of the source calendar account (for grouping/labeling personal vs work). */
  accountEmail: string | null;
  title: string;
  start: string; // ISO 8601
  end: string; // ISO 8601
  allDay: boolean;
  /** Google `endTimeUnspecified` — the end time is a placeholder, not a real duration. */
  endUnspecified: boolean;
  /** Google Meet link, when the event has one. */
  hangoutLink: string | null;
  /** Google `colorId` (e.g. "1", "8"); null when the event uses its calendar's default color. */
  colorId: string | null;
  /** "transparent" (shown as free) or "opaque" (busy). */
  transparency: string | null;
  attendeeCount: number;
  /** Whether the viewing user is an attendee. */
  self: boolean;
}

/** The agenda sidebar's payload for one day: the events plus the day they belong to. */
export interface AgendaDayDTO {
  dateKey: string; // YYYY-MM-DD
  events: AgendaEventDTO[];
}

export interface LinkedAccountDTO {
  accountId: string;
  userId: string;
  provider: ProviderId;
  externalId: string;
  email?: string;
  scopes: string[];
  integrations: Integration[];
  createdAt: number;
}

export interface MeResponse {
  user: { userId: string; email?: string; createdAt: number; preferences: Record<string, unknown> };
  linkedAccounts: LinkedAccountDTO[];
  /** SYSTEM-HEALTH — true when the signed-in email is on the server ADMIN_EMAILS allowlist. Gates the
   * admin system-health indicator + detail panel. Older servers omit it → treated as false. */
  isAdmin?: boolean;
}

export interface OAuthLinkStart {
  authUrl: string;
  state: string;
}

/** One linked Slack workspace + its per-workspace config, from `/me/slack/workspaces`. */
export interface SlackWorkspaceView {
  accountId: string;
  teamId: string;
  teamName: string;
  enabled: boolean;
  allow: string[];
  deny: string[];
}

/** Result of a manual Slack digest run. */
export interface SlackRunResultDTO {
  workspaces: number;
  channelsRead: number;
  items: number;
  truncated: boolean;
  skippedChannels: number;
  quiet: boolean;
  posted: boolean;
}

export interface SlackDraftRequestDTO {
  teamId: string;
  channelLabel?: string;
  summary?: string;
  text: string;
  threadContext?: string[];
}

export interface SlackSendRequestDTO {
  teamId: string;
  channelId: string;
  threadTs?: string;
  text: string;
}

/** One linked Gmail mailbox, as shown on the Gmail Cleanup detail page. */
export interface CleanupAccountDTO {
  accountId: string;
  email: string;
  enabled: boolean;
  /** Whether this mailbox has granted the gmail.modify write scope (else it shows "Grant delete permission"). */
  hasDeletePermission: boolean;
}

/** Gmail Cleanup detail payload: config + per-account rows + the last run. */
export interface CleanupDetailDTO {
  config: GmailCleanupConfig;
  accounts: CleanupAccountDTO[];
  lastRun: UtilityRun | null;
}

/** Resolution of a `[[name]]` link to its page note (IMPL-07). exists:false ⇒ create the page. */
export interface PageResolutionDTO {
  name: string;
  entityId: string;
  noteId: string | null;
  title: string | null;
  exists: boolean;
}

/** A single inbound reference, with the source block's text as context. */
export interface BacklinkRefDTO {
  kind: 'wikilink' | 'tag';
  context: string;
}

/** All references to a page from one source note — a card in the Linked References list. */
export interface BacklinkGroupDTO {
  sourceNoteId: string;
  sourceTitle: string;
  sourceType: string;
  updatedAt: number;
  refs: BacklinkRefDTO[];
}

export interface BacklinksDTO {
  /** Total references across every matching source note — the "N Linked References" header count. */
  total: number;
  /** Total matching source-note cards. `groups` is one page-sized slice of these. */
  totalGroups: number;
  /** Offset of this slice into the sorted/filtered card list. */
  offset: number;
  /** Cards requested (echoed); equals `totalGroups` when no limit was passed. */
  limit: number;
  groups: BacklinkGroupDTO[];
}

/** Paging/search for {@link ApiClient.getBacklinks}. */
export interface BacklinksOpts {
  limit?: number;
  offset?: number;
  query?: string;
}

/** An addressable block (IMPL-07) — drives the `((` picker and `((id))`/block-embed resolution. */
export interface BlockDTO {
  userId: string;
  blockId: string;
  noteId: string;
  text: string;
  updatedAt: number;
}

/** A full-content search hit for the Cmd+K palette: a note whose body text matched, with a snippet. */
export interface ContentHitDTO {
  noteId: string;
  title: string;
  type: string;
  /** A short window of the matching text, centered on the query. */
  snippet: string;
  /** The matching block's id (`b<index>`), so opening the result can scroll to that exact block. */
  blockId?: string;
  /** LENS-04 — the matching block's ancestor heading path (' › '-joined), shown as a breadcrumb. */
  crumbs?: string;
}

/** One hit in a cross-corpus palette group (LENS-03). Corpus-generic; today only `contact` is wired. */
export interface PaletteHitDTO {
  id: string;
  corpus: string;
  noteId: string | null;
  blockId: string | null;
  title: string;
  snippet: string;
  crumbs?: string;
}

/** A corpus's slice of grouped palette results. */
export interface PaletteGroupDTO {
  corpus: string;
  hits: PaletteHitDTO[];
  matchedCount: number;
}

/** Read-only content of a page, for a `{{embed [[Page]]}}` preview. */
export interface PageContentDTO {
  noteId: string;
  title: string;
  text: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ── CREEL — Amazon via Clavero (shopping) ──────────────────────────────────────────────────────────

/** One order line requested when drafting a purchase intent (POST /me/shopping/intents). */
export interface ShoppingIntentLineInput {
  asin: string;
  qty: number;
  sku?: string;
}

/** The outcome of drafting (or restoring) a purchase intent — a discriminated union over the
 *  server's intent-draft responses (§6.1). Drafting is async: `building` means Clavero is still
 *  building/pricing the order (~2-6 min) and the client should poll it; `failed` means the build
 *  was rejected (e.g. a Clavero guardrail). */
export type DraftIntentResult =
  | { status: 'building'; intent: LapoOrderIntent }
  | { status: 'drafted'; intent: LapoOrderIntent; autoConfirmed: boolean }
  | { status: 'cart-non-empty'; cartItemCount: number }
  | { status: 'guardrail'; control: string }
  | { status: 'needs-human'; detail: string }
  | { status: 'failed'; detail?: string };

/** POST /me/shopping/search — verified candidates, or a graceful-degradation note when search is down. */
export interface ShoppingSearchResult {
  candidates: ProductCandidate[];
  degraded?: boolean;
  reason?: string;
}

/** Bill Pay config as WRITTEN from the UI: a card carries a raw PAN/CVV and a bank account a raw
 * routing/account pair, which the server encrypts + strips. */
export type BillPayConfigWrite = Omit<BillPayConfig, 'paymentMethods' | 'bankAccounts'> & {
  paymentMethods: Array<PaymentMethodMeta & { number?: string; cvv?: string }>;
  bankAccounts: Array<BankAccountMeta & { routingNumber?: string; accountNumber?: string }>;
};

/**
 * A client-side crash report (UX-001). The web/desktop shell POSTs render throws, uncaught errors, and
 * failed lazy-chunk loads to /client-errors, which logs one structured line for Cloud Logging / OBS-001.
 */
export interface ClientErrorReport {
  kind: 'render' | 'onerror' | 'unhandledrejection' | 'chunk-load';
  message: string;
  stack?: string;
  /** Where it came from — a component stack, or `file:line` for a window error. */
  source?: string;
  appVersion?: string;
  platform?: string;
}

// ── KUDOS (employee thank-you letters) DTOs — mirror the server's /kudos/* responses ────────────────
/** CHASE — nightly follow-up list config (mirrors @lapo/core ChaseConfig). */
export interface ChaseConfigDTO {
  enabled: boolean;
  cap: number;
  floorBusinessDays: number;
  staleDays: number;
  maxThreadAgeDays: number;
  handledCooldownDays: number;
  suppression: 'off' | 'shadow' | 'enforce';
}

export interface KudosConfigDTO {
  enabled: boolean;
  assistantReads?: boolean;
  weeklyBudget: number;
  anniversaryLeadDays: number;
  stalenessMonths: number;
  miningBackfillDays: number;
  freshWindowDays: number;
  globalPrompt: string;
  lastSyncAt?: number;
  nextCardNumberOverride?: number;
}
export interface KudosTriggerContributionDTO {
  trigger: string;
  strength: number;
  weighted: number;
}
export interface KudosSuggestionFactorsDTO {
  tenureMonths: number | null;
  monthsSinceThanked: number | null;
  daysToAnniversary: number | null;
  freshWins: number;
}
export interface KudosSuggestionDTO {
  id: string;
  employeeId: string;
  score: number;
  triggers: string[];
  reason: string;
  contributions?: KudosTriggerContributionDTO[];
  factors?: KudosSuggestionFactorsDTO;
  evidence?: string[];
  highlightIds: string[];
  status: string;
  anniversaryOverride?: boolean;
  releasedWeek?: string;
}
export interface KudosRosterRowDTO {
  employeeId: string;
  name: string;
  title?: string;
  department?: string;
  lastThankedAt?: number;
  thankCount: number;
  daysToAnniversary: number | null;
  anniversaryKind?: 'hire' | 'role';
  muted: boolean;
  pinned: boolean;
  suggestable: boolean;
}
export interface KudosLetterDTO {
  id: string;
  employeeId: string;
  displayName: string;
  cardNumber: number;
  dateKey: string;
  text: string;
  createdAt: number;
  source: string;
}

export interface ApiClient {
  getMe(): Promise<MeResponse>;
  /** Report a client-side crash (best-effort forensics; server just logs it). */
  postClientError(report: ClientErrorReport): Promise<{ ok: boolean }>;
  /** SYSTEM-HEALTH (admin) — the backend health snapshot the indicator polls. 403 for non-admins. */
  getHealth(): Promise<HealthSnapshot>;
  /** SYSTEM-HEALTH (admin) — the curated recent health-event feed for the detail panel. */
  getHealthEvents(limit?: number): Promise<{ events: HealthEventDTO[] }>;
  /** SYSTEM-HEALTH (admin) — on-demand raw Cloud Logging drill-down (ApiError(503) when unavailable). */
  getAdminLogs(opts?: { severity?: string; windowMs?: number; pageSize?: number }): Promise<{ entries: RawLogEntry[] }>;
  /**
   * Update the signed-in user's profile; persisted server-side so it syncs across devices. Alongside
   * the display name, carries the AXIS identity/autonomy preferences (X24/X25/X27/X22): biography,
   * per-mailbox identities, action-target defaults, and the master autonomy flag.
   */
  updateProfile(patch: {
    displayName?: string;
    biography?: string;
    contact?: UserContact;
    accountIdentities?: Record<string, { displayName?: string; signoff?: string; role?: string }>;
    actionDefaults?: { todoistProjectId?: string; taskLabel?: string; calendarId?: string };
    axisAutonomy?: boolean;
    // QUILL — writing voice (D5), working-hours window (C0/D11), and the style flag (D18).
    writingStyle?: WritingStyleProfile;
    workingHours?: WorkingHours;
    quillVoice?: boolean;
    /** SIEVE S10 — per-source ingest-triage modes; server merges + coerces. */
    ingestTriage?: { instagram?: 'off' | 'shadow' | 'enforce'; emailInbox?: 'off' | 'shadow' | 'enforce' };
    /** FOLD — customized left-nav layout (order + separator + collapsed); server coerces on write. */
    menuLayout?: MenuLayout;
  }): Promise<MeResponse>;
  /** QUILL-05 — draft a writing-voice profile from the owner's recent sent mail (returns it PROPOSED). */
  bootstrapWritingStyle(): Promise<{ profile: WritingStyleProfile; sampledMessages: number; me: MeResponse }>;
  /**
   * Mint a self-issued long-lived bearer token for the native mobile "Share to Lapo" handler, which
   * runs outside the WebView and so can't reuse the Firebase session. Re-minting supersedes the prior
   * token; it's revocable in Settings → Authorized apps. Returns the raw token exactly once.
   */
  getShareToken(): Promise<{ token: string }>;
  beginLink(provider: ProviderId, scopes?: string[]): Promise<OAuthLinkStart>;
  completeLink(state: string, code: string): Promise<LinkedAccountDTO>;
  /**
   * Server-brokered Google link (mobile). Returns the consent URL to open + a sessionId to poll. The
   * browser callback deposits the code server-side (the app never needs to receive it — the Android
   * deep-link handoff is unreliable); {@link pollLinkBroker} reports completion.
   */
  startLinkBroker(scopes?: string[]): Promise<{ authUrl: string; sessionId: string }>;
  /** Server-brokered Slack link (mobile) — same deposit/poll path as {@link startLinkBroker}, but for a
   *  Slack workspace. Poll with the shared {@link pollLinkBroker}. */
  startSlackLinkBroker(): Promise<{ authUrl: string; sessionId: string }>;
  /** Server-brokered Todoist link (mobile) — same deposit/poll path as {@link startLinkBroker}, but for
   *  a Todoist account. Poll with the shared {@link pollLinkBroker}. */
  startTodoistLinkBroker(): Promise<{ authUrl: string; sessionId: string }>;
  /** Poll a brokered link for completion. `status` is 'pending' until the browser deposit lands. */
  pollLinkBroker(sessionId: string): Promise<{ status: 'pending' | 'done' | 'error' | 'unknown'; accountId?: string; error?: string }>;
  /** Begin the Todoist OAuth link: returns the consent URL + state to run through the redirect handler. */
  beginTodoistLink(): Promise<OAuthLinkStart>;
  /** Complete the Todoist OAuth link with the code+state captured from the redirect. */
  completeTodoistLink(state: string, code: string): Promise<LinkedAccountDTO>;
  /** Begin the Tesseta (Fitness) OAuth link (Authorization Code + PKCE): returns the consent URL + state. */
  beginTessetaLink(): Promise<OAuthLinkStart>;
  /** Complete the Tesseta OAuth link with the code+state captured from the redirect (kicks an initial sync). */
  completeTessetaLink(state: string, code: string): Promise<LinkedAccountDTO>;
  /** Link EasyPost from a pasted API key (stored KMS-encrypted server-side). */
  linkEasyPost(apiKey: string): Promise<LinkedAccountDTO>;
  /** Link Claude from a pasted subscription token / BYO key (stored KMS-encrypted server-side). */
  linkClaude(apiKey: string): Promise<LinkedAccountDTO>;
  /** Link Granola from a pasted API key (stored KMS-encrypted server-side). */
  linkGranola(apiKey: string): Promise<LinkedAccountDTO>;
  /** Read the user's Granola poller config (seeded defaults if unset). */
  getGranolaConfig(): Promise<GranolaConfig>;
  /** Persist Granola poller config; the server coerces/validates before storing. */
  putGranolaConfig(config: GranolaConfig): Promise<GranolaConfig>;
  /** Begin the Slack user-token OAuth link (returns the authorize URL + state for the popup). */
  startSlackLink(): Promise<OAuthLinkStart>;
  /** Complete the Slack OAuth link (exchange the popup's code); links one workspace. */
  completeSlackLink(input: { state: string; code: string }): Promise<LinkedAccountDTO>;
  /** List the user's linked Slack workspaces + their per-workspace config. */
  listSlackWorkspaces(): Promise<{ workspaces: SlackWorkspaceView[] }>;
  /** Unlink one Slack workspace (purges its config, cursors, and cached messages). */
  unlinkSlackWorkspace(accountId: string, teamId: string): Promise<void>;
  /** Read the user's global Slack config (seeded defaults if unset). */
  getSlackConfig(): Promise<SlackConfig>;
  /** Persist the global Slack config; the server coerces/validates before storing. */
  putSlackConfig(config: SlackConfig): Promise<SlackConfig>;
  /** Update one workspace's config (enable + allow/deny). */
  putSlackWorkspace(teamId: string, config: SlackWorkspaceCfg): Promise<unknown>;
  /** Run the Slack digest now (composes + posts today's block). */
  runSlackDigest(): Promise<SlackRunResultDTO>;
  /** Draft a reply to a Slack digest item (composes; never sends). */
  draftSlackReply(input: SlackDraftRequestDTO): Promise<{ text: string }>;
  /** Send an approved Slack reply as the user. */
  sendSlackReply(input: SlackSendRequestDTO): Promise<{ ts: string }>;
  /** Read the user's Gemini meeting-notes poller config (seeded defaults if unset). */
  getGeminiConfig(): Promise<GeminiConfig>;
  /** Persist Gemini poller config; the server coerces/validates before storing. */
  putGeminiConfig(config: GeminiConfig): Promise<GeminiConfig>;
  /** List the user's Todoist projects (for the enricher project pickers); linked=false when unconnected. */
  getTodoistProjects(): Promise<{ linked: boolean; projects: TodoistProject[]; error?: string; needsReconnect?: boolean }>;
  /** Read the user's Movie Enricher config (seeded defaults if unset). */
  getMovieEnricherConfig(): Promise<MovieEnricherConfig>;
  /** Persist Movie Enricher config; the server coerces/validates before storing. */
  putMovieEnricherConfig(config: MovieEnricherConfig): Promise<MovieEnricherConfig>;
  /** Read the user's Restaurant Verifier config (seeded defaults if unset). */
  getRestaurantVerifierConfig(): Promise<RestaurantVerifierConfig>;
  /** Persist Restaurant Verifier config; the server coerces/validates before storing. */
  putRestaurantVerifierConfig(config: RestaurantVerifierConfig): Promise<RestaurantVerifierConfig>;
  /** Read the user's Instagram Share config (the key comes back as the sentinel, never raw). */
  getInstagramShareConfig(): Promise<InstagramShareConfig>;
  /** Persist Instagram Share config (raw key, or '' to clear); returns the masked config. */
  putInstagramShareConfig(config: InstagramShareConfig): Promise<InstagramShareConfig>;
  /** Read the user's Podcast Summaries config (vendor keys come back masked). */
  getPodcastConfig(): Promise<PodcastConfig>;
  /** Persist Podcast Summaries config; returns the masked config. */
  putPodcastConfig(config: PodcastConfig): Promise<PodcastConfig>;
  /** Start a podcast/video summary run (batch paste). Returns the run view. */
  runPodcasts(req: { instruction: string; blob?: string; inputs?: string[]; detail?: DetailLevel; audience?: string; enrichImages?: boolean; target: { kind: OutputTargetKind; dateKey?: string } }): Promise<PodcastRunView>;
  /** Recent podcast runs for the status list. */
  listPodcastRuns(limit?: number): Promise<{ runs: PodcastRunView[] }>;
  /** Summarize one link from the `/podcast` slash command; the client placed the placeholder (place='client'). */
  slashPodcast(link: string, opts?: { dateKey?: string; itemId?: string }): Promise<{ runId: string; placeholder?: { noteId: string; sectionId: string } }>;
  /** Read the user's Voice Calling config (API key comes back masked). */
  getVoiceConfig(): Promise<VoiceConfig>;
  /** Persist Voice Calling config; returns the config plus whether the assistant's webhook auto-configured. */
  putVoiceConfig(config: VoiceConfig): Promise<VoiceConfig & { webhookConfigured?: boolean; webhookError?: string }>;
  /** Fetch the user's Vapi assistants + phone numbers (with their stored key) for the config dropdowns. */
  getVoiceResources(): Promise<VoiceResources>;
  /** Read the user's Bill Pay config (card PAN/CVV + Bright Data endpoint come back masked). */
  getBillPayConfig(): Promise<BillPayConfig>;
  /** Persist Bill Pay config (cards may carry a raw PAN/CVV the server encrypts + strips); returns masked. */
  putBillPayConfig(config: BillPayConfigWrite): Promise<BillPayConfig>;
  /** Delete a stored card (removes its metadata + encrypted secret); returns the masked config. */
  deletePaymentMethod(cardId: string): Promise<BillPayConfig>;
  /** Delete a stored bank account (removes its metadata + encrypted secret); returns the masked config. */
  deleteBankAccount(bankId: string): Promise<BillPayConfig>;
  /** Read the user's Reddit Monitor config (redditapis key comes back masked). */
  getRedditConfig(): Promise<RedditConfig>;
  /** Persist Reddit Monitor config (channels + filters + BYO key); returns the masked config. */
  putRedditConfig(config: RedditConfig): Promise<RedditConfig>;
  /** Live-fetch the monitored channels' newest posts, filtered to the configured search terms. */
  getRedditResults(): Promise<RedditResults>;
  /** Import an Instagram post by URL into the day's journal (same pipeline as the phone share). */
  postInstagramImport(url: string, date: string, opts?: InstagramImportOptions): Promise<InstagramImportResult>;
  /** Re-scan a finished/failed import in place — redoes the entire post import (journal "Re-scan"). */
  rescanInstagramImport(importId: string): Promise<InstagramImportResult>;
  /** Resolve a URL's link-preview metadata for a bookmark card (null when the page couldn't be read). */
  resolveLinkMetadata(url: string): Promise<BookmarkMeta | null>;
  unlink(accountId: string): Promise<void>;
  /** Register/refresh this device's FCM token for wake-and-sync push (idempotent per deviceId). */
  registerDevice(deviceId: string, token: string, platform?: string): Promise<void>;
  /** Drop this device's push token (e.g. on sign-out). */
  unregisterDevice(deviceId: string): Promise<void>;
  /** Today panel: overdue + today tasks, ordered/colored as the Todoist UI. `date` is the client's local YYYY-MM-DD. */
  getTodoistToday(date: string): Promise<TodoistTodayDTO>;
  /** Complete (close) a Todoist task in the given linked account. `needsReconnect` = the token died. */
  completeTodoistTask(accountId: string, taskId: string): Promise<{ ok: boolean; needsReconnect?: boolean }>;
  /** Read the user's Daily Agenda settings (seeded defaults if unset). */
  getAgendaConfig(): Promise<AgendaSettings>;
  /** Persist Daily Agenda settings; the server coerces/validates before storing. */
  putAgendaConfig(settings: AgendaSettings): Promise<AgendaSettings>;
  /** Read the user's toolbar-note (scratchpad) config (seeded defaults if unset). */
  getToolbarConfig(): Promise<ToolbarConfig>;
  /** Persist toolbar-note config; the server coerces/validates before storing. */
  putToolbarConfig(config: ToolbarConfig): Promise<ToolbarConfig>;
  /** List the user's Google calendars with their resolved watched state. */
  getCalendars(): Promise<{ calendars: UserCalendarDTO[] }>;
  /** Read the user's per-calendar watch overrides. */
  getCalendarPrefs(): Promise<CalendarPrefs>;
  /** Persist per-calendar watch overrides; the server re-registers watches to match. */
  putCalendarPrefs(prefs: CalendarPrefs): Promise<CalendarPrefs>;
  /** List the available processing engines (Automations catalog). */
  getEngineCatalog(): Promise<{ engines: EngineCatalogEntry[] }>;
  /** Read the user's engine-pipeline config (empty pipeline if unset). */
  getEngineConfig(): Promise<EngineConfig>;
  /** Persist engine-pipeline config; the server coerces/validates before storing. */
  putEngineConfig(config: EngineConfig): Promise<EngineConfig>;
  /** Compose the agenda for a day on demand; returns the Logseq markdown + event count. */
  runAgenda(dateKey: string, timeZone?: string): Promise<{ markdown: string | null; eventCount: number }>;
  /** Agenda sidebar: every event across the user's calendars for a day. `dateKey` is YYYY-MM-DD. */
  getAgendaDay(dateKey: string): Promise<AgendaDayDTO>;
  /** Integrations hub: every workload with its live status + most-recent action. */
  listIntegrations(): Promise<HubIntegration[]>;
  /** NEXUS: the per-user capability manifest — the same list the assistant sees. */
  getCapabilities(): Promise<CapabilityManifest>;
  /** One integration with its full audit log; null when the id is unknown. */
  getIntegration(id: string): Promise<HubIntegration | null>;
  /** Trigger a runnable integration on demand; returns its refreshed detail. */
  runIntegration(id: string): Promise<HubIntegration>;
  /** The per-user master on/off map for every integration (absent id → enabled). */
  getIntegrationEnablementMap(): Promise<Record<string, boolean>>;
  /** Flip an integration's master on/off switch; returns its refreshed detail (null if id unknown). */
  setIntegrationEnabled(id: string, enabled: boolean): Promise<HubIntegration | null>;
  // --- Utilities (maintenance workloads) ---
  /** Utilities hub: every utility with its live status + most-recent run. */
  listUtilities(): Promise<Utility[]>;
  /** Gmail Cleanup detail: config, per-account rows (email/enabled/delete-permission), and the last run. */
  getUtilityDetail(id: string): Promise<CleanupDetailDTO>;
  /** Read a utility's config (seeded defaults if unset). */
  getUtilityConfig(id: string): Promise<GmailCleanupConfig>;
  /** Persist a utility's config; the server coerces/clamps before storing. */
  putUtilityConfig(id: string, config: GmailCleanupConfig): Promise<GmailCleanupConfig>;
  /** Poll the last-run doc (live progress + result); null before the first run. */
  getUtilityStatus(id: string): Promise<UtilityRun | null>;
  /** Dry-run: compute matches and pause for approval. Never trashes. */
  previewUtility(id: string): Promise<UtilityRun>;
  /** Execute: approve a preview (`approveRunId`) or, once trusted, run directly. Trashes matched mail. */
  runUtility(id: string, approveRunId?: string): Promise<UtilityRun>;
  /** Begin the separate Google re-consent that grants the gmail.modify delete permission. */
  beginGmailModify(): Promise<OAuthLinkStart>;
  /** Read the user's Subscription Digest config (seeded defaults if unset). */
  getSubscriptionConfig(): Promise<SubscriptionDigestConfig>;
  /** Persist Subscription Digest config; the server coerces/validates before storing. */
  putSubscriptionConfig(config: SubscriptionDigestConfig): Promise<SubscriptionDigestConfig>;
  /** The most recent digest issue, or null when none has been generated. */
  getSubscriptionCurrent(): Promise<SubscriptionIssue | null>;
  /** Back-issue summaries, newest first. */
  listSubscriptionIssues(): Promise<SubscriptionIssueSummary[]>;
  /** One digest issue by id; null when unknown. */
  getSubscriptionIssue(id: string): Promise<SubscriptionIssue | null>;
  /** Append a single tuning rule (STEER inline quick-add); server stamps id + createdAt. */
  addSubscriptionRule(rule: { match: SubscriptionRuleMatch; pattern: string; action: SubscriptionRuleAction; note?: string }): Promise<SubscriptionRule>;
  /** Remove a tuning rule by id. */
  deleteSubscriptionRule(id: string): Promise<void>;
  /** The reader's stored thumbs ratings (newest first) — drives thumb button state. */
  listSubscriptionFeedback(): Promise<SubscriptionFeedbackEntry[]>;
  /** Rate a digest story ('clear' withdraws); returns the updated list. */
  setSubscriptionFeedback(input: { issueId: string; storyId: string; verdict: SubscriptionFeedbackVerdict | 'clear' }): Promise<SubscriptionFeedbackEntry[]>;
  // --- Financial Management (Plaid) ---
  /** Read the user's Financial Management config (seeded defaults if unset). */
  getFinancialConfig(): Promise<FinancialConfig>;
  /** Persist Financial Management config; the server coerces/validates before storing. */
  putFinancialConfig(config: FinancialConfig): Promise<FinancialConfig>;
  /** Mint a Plaid Link token for the client's embedded Plaid Link handoff (web). */
  createPlaidLinkToken(): Promise<{ linkToken: string; expiration: string }>;
  /** Mint a Plaid HOSTED Link token (native apps): a URL to open in the system browser. */
  createHostedPlaidLink(): Promise<{ linkToken: string; hostedLinkUrl: string; expiration: string }>;
  /** Mint an update-mode Link token to reconnect a disconnected item in place (web embedded Link). */
  createPlaidUpdateLinkToken(itemId: string): Promise<{ linkToken: string; expiration: string }>;
  /** Mint a HOSTED update-mode Link token to reconnect a disconnected item (native, system browser). */
  createHostedPlaidUpdateLink(itemId: string): Promise<{ linkToken: string; hostedLinkUrl: string; expiration: string }>;
  /** Poll a hosted-link session for completion; exchanges finished tokens. `pending` = user not done yet. */
  completeHostedPlaid(linkToken: string): Promise<{ connected: number; duplicates: number; pending: boolean }>;
  /** Exchange a Plaid public_token (from Plaid Link) into a stored bank connection. */
  exchangePlaidPublicToken(publicToken: string): Promise<{ itemId: string; institutionName: string }>;
  /** List the user's connected banks. */
  listFinancialItems(): Promise<FinancialItemDTO[]>;
  /** Distinct cardholders (Amex-style sub-cards) + their current aliases, for the settings editor. */
  listFinancialCardholders(): Promise<{ raw: string; alias: string }[]>;
  /** Disconnect one bank (revokes at Plaid + purges its data). */
  removeFinancialItem(itemId: string): Promise<void>;
  /** Purge ALL financial data + disconnect every bank. */
  purgeFinancial(): Promise<void>;
  /** The dashboard summary: net worth, accounts, category spend, owner split, trend, recurring, alerts. */
  getFinancialSummary(): Promise<FinancialSummaryDTO>;
  /** The Fitness summary (latest weight/body-comp, compliance, streak) + the user's Tesseta config. */
  getFitnessSummary(): Promise<FitnessSummaryResponse>;
  /** Body-composition + DEXA series for the trend charts (trailing `sinceDays`, default ~2 years). */
  getBodyComposition(sinceDays?: number): Promise<BodyCompositionSeriesDTO>;
  /** Per-program workout compliance + streak + the calendar heatmap cells. */
  getWorkoutCompliance(): Promise<WorkoutComplianceViewDTO>;
  /** Run the Tesseta sync on demand ("Sync now"). */
  syncFitness(): Promise<{ bodyComp: number; dexa: number; programs: number; workouts: number }>;
  /** Toggle the sensitive assistant-reads opt-in for Fitness data. */
  setFitnessAssistantReads(enabled: boolean): Promise<{ assistantReads: boolean }>;
  /** Set the display unit for weight / lean mass (lb or kg). */
  setFitnessWeightUnit(unit: 'lb' | 'kg'): Promise<{ weightUnit: 'lb' | 'kg' }>;
  // ── CHASE — nightly follow-up list ──────────────────────────────────────────────────────────────
  getChaseConfig(): Promise<ChaseConfigDTO>;
  putChaseConfig(patch: Partial<ChaseConfigDTO>): Promise<ChaseConfigDTO>;
  // ── KUDOS — employee thank-you letters ──────────────────────────────────────────────────────────
  getKudosConfig(): Promise<KudosConfigDTO>;
  putKudosConfig(patch: Partial<KudosConfigDTO>): Promise<KudosConfigDTO>;
  getKudosRoster(): Promise<{ roster: KudosRosterRowDTO[] }>;
  getKudosSuggestions(): Promise<{ suggestions: KudosSuggestionDTO[] }>;
  getKudosHistory(employeeId: string): Promise<{ letters: KudosLetterDTO[] }>;
  /** The latest notes written across everyone (newest first) — the Suggestions activity feed. */
  getKudosRecentLetters(): Promise<{ letters: KudosLetterDTO[] }>;
  writeKudosLetter(body: {
    employeeId: string;
    text: string;
    sourceSuggestionId?: string;
    highlightIds?: string[];
    aiDrafted?: boolean;
    editedFromDraft?: boolean;
  }): Promise<{ letter: KudosLetterDTO }>;
  snoozeKudosSuggestion(id: string, days?: number): Promise<void>;
  dismissKudosSuggestion(id: string): Promise<void>;
  repullKudos(): Promise<{ released: number; setAside: number }>;
  updateKudosPerson(
    employeeId: string,
    patch: { muted?: boolean; pinned?: boolean; aliases?: string[]; lastThankedAt?: number },
  ): Promise<void>;
  /** Draft a thank-you note on demand (owner's Claude model). Rejects when no model is connected. */
  draftKudosLetter(employeeId: string): Promise<{ text: string }>;
  syncKudos(): Promise<{ employees: number }>;
  setKudosAssistantReads(enabled: boolean): Promise<void>;
  /** Connect Rippling with a company API key (verified server-side; links the account + enables KUDOS). */
  linkRippling(apiKey: string): Promise<void>;
  // ── CREEL — Amazon via Clavero ──────────────────────────────────────────────────────────────────
  /** The user's masked Clavero config (tokens are the sentinel `__SAVED__` or ''). */
  getClaveroConfig(): Promise<ClaveroConfig>;
  /** Save the Clavero config — send a raw token to set it, `__SAVED__`/'' to keep. The server runs a
   * live `/v1/health` check; a failure throws ApiError(400, 'clavero-health-check-failed'). */
  putClaveroConfig(config: ClaveroConfig): Promise<ClaveroConfig>;
  /** The composite shopping summary (health/spend, cart, stash, pending intents, recent orders, catalog). */
  getShopping(): Promise<ShoppingSummary>;
  /** Force a live cart + health read, then return a fresh summary. */
  refreshShopping(): Promise<ShoppingSummary>;
  /** Search Amazon for products (D5 bridge) — verified candidates, or a degraded note when search is down. */
  searchShopping(query: string): Promise<ShoppingSearchResult>;
  /** A live product read (drives Chrome, seconds-slow) for the paste-ASIN preview. */
  getShoppingProduct(asin: string): Promise<ProductRead>;
  /** Draft a priced purchase intent from lines; pass `allowStashClear` after a cart-non-empty consent. */
  draftShoppingIntent(lines: ShoppingIntentLineInput[], allowStashClear?: boolean): Promise<DraftIntentResult>;
  /** The intents audit trail (optionally filtered by status / limited). */
  listShoppingIntents(opts?: { limit?: number; status?: string }): Promise<{ intents: LapoOrderIntent[] }>;
  /** One intent by id. */
  getShoppingIntent(id: string): Promise<LapoOrderIntent>;
  /** Confirm a pending intent (places the order). 409 drift/lock, 423 needs-human, 403 guardrail. */
  confirmShoppingIntent(id: string, surface: Extract<ConfirmSurface, 'web' | 'mobile'>): Promise<LapoOrderIntent>;
  /** Cancel a pending intent. */
  cancelShoppingIntent(id: string): Promise<LapoOrderIntent>;
  /** Set the quantity of a cart line (physical Amazon cart). */
  setCartQty(asin: string, qty: number): Promise<ShoppingCartSnapshot>;
  /** Remove a cart line. */
  removeCartLine(asin: string): Promise<ShoppingCartSnapshot>;
  /** Clear the cart (records a stash if it was non-empty). */
  clearCart(): Promise<ShoppingCartSnapshot>;
  /** Restore the stash — drafts a NEW priced purchase intent from the stashed lines (D7). */
  restoreStash(): Promise<DraftIntentResult>;
  /** Discard the stash without re-ordering (the ✕ on the "Stashed cart" card). */
  discardStash(): Promise<{ ok: boolean }>;
  /** Place the current cart as a priced order (stash+clear+draft); honors the auto-confirm dial for small orders. */
  placeCartOrder(): Promise<DraftIntentResult>;
  /** Synced Amazon order history (`refresh` forces a live sync first). */
  getShoppingOrders(refresh?: boolean): Promise<{ orders: AmazonOrder[] }>;
  /** TAXON — Amazon spend grouped by canonical bucket (Shopping-page "Spend by category"). */
  getShoppingBuckets(): Promise<AmazonBucketRollup>;
  /** One synced Amazon order by id. */
  getShoppingOrder(orderId: string): Promise<AmazonOrder>;
  /** The Amazon match for a finance transaction (status `unmatched` when there is none). */
  getShoppingMatch(txnId: string): Promise<AmazonTxnMatch>;
  /** Resolve an ambiguous match: pick a charge (`{ chargeId }`) or reject (`{ reject: true }`). */
  resolveShoppingMatch(txnId: string, input: { chargeId: string } | { reject: true }): Promise<AmazonTxnMatch>;
  /** TAXON — correct a line's bucket: pin the ASIN (and optionally the whole breadcrumb) to a coarse
   * `bucket`, plus an optional finer `category` (e.g. "Home Improvement") shown as the line label. */
  setShoppingBucket(txnId: string, input: { asin: string; bucket: string; category?: string; breadcrumb?: string; applyToBreadcrumb?: boolean }): Promise<{ updated: number }>;
  /** The user's category taxonomy grouped by bucket (from the Monarch import + manual choices). */
  getFinancialCategories(): Promise<CategoryGroup[]>;
  /** The transaction feed (filterable). */
  listFinancialTransactions(query?: TransactionQuery): Promise<FinancialTransaction[]>;
  /** Detected recurring streams (subscriptions/bills), incl. duplicate + funding-source flags. */
  listRecurring(): Promise<RecurringStream[]>;
  /** Justify/recategorize a transaction; `applyToPast` creates a rule + retro-applies (D20). */
  justifyTransaction(txnId: string, category: string, applyToPast: boolean): Promise<{ updated: number; rule: boolean }>;
  /** Rename a recurring stream or mark it not-recurring (persisted override). */
  setRecurringOverride(key: string, patch: { displayName?: string; dismissed?: boolean }): Promise<void>;
  /** Rename a vendor everywhere (its charges + recurring stream). Empty string clears the rename. */
  setMerchantName(merchant: string, displayName: string): Promise<void>;
  /** List the user's automation rules (category rules + vendor renames). */
  listFinancialRules(): Promise<FinancialRulesDTO>;
  /** Delete a category rule (stops future auto-apply; already-categorized charges untouched). */
  deleteCategoryRule(ruleId: string): Promise<void>;
  /** STENCIL — dry-run a rule draft; returns the count + samples of matching past transactions (D9). */
  previewFinancialRule(draft: { conditions: RuleCondition[]; category: string }): Promise<{ count: number; samples: FinancialTransaction[] }>;
  /** STENCIL — create a multi-condition category rule (optionally retro-applied to history). */
  createFinancialRule(draft: { conditions: RuleCondition[]; category: string; priority?: number }, applyToPast: boolean): Promise<{ rule: { ruleId: string }; applied: number }>;
  /** STENCIL — edit a category rule's conditions/category/priority (optionally retro-applied). */
  updateFinancialRule(ruleId: string, patch: { conditions?: RuleCondition[]; category?: string; priority?: number }, applyToPast: boolean): Promise<{ rule: { ruleId: string }; applied: number } | null>;
  /** Create/update a vendor rename rule (exact or regex). */
  putRenameRule(rule: { id?: string; pattern: string; isRegex: boolean; displayName: string }): Promise<RenameRuleDTO>;
  /** Delete a vendor rename rule by id. */
  deleteRenameRule(id: string): Promise<void>;
  /** Run an LLM audit of the rules; returns removals + regex-consolidation suggestions. */
  auditFinancialRules(): Promise<RuleAuditDTO>;
  /** Dismiss a financial alert. */
  dismissFinancialAlert(alertId: string): Promise<void>;
  /** Import a Monarch Money CSV export (back-applies curated names/categories + seeds rules). */
  importMonarchCsv(csv: string, fileName?: string): Promise<MonarchImportResultDTO>;
  /** Upload raw file bytes to private blob storage; returns the stable `lapo://` reference. */
  uploadBlob(file: File): Promise<UploadedBlobDTO>;
  /** Mint a short-lived signed read URL for a stored `lapo://` blob. */
  signBlob(uri: string): Promise<SignedUrlDTO>;
  /** Contact Pictures studio: generate a headshot draft from uploaded reference photos. */
  generateHeadshot(input: GenerateHeadshotInput): Promise<GenerateHeadshotResult>;
  /** Contact Pictures studio: approve a draft, writing it back to Google Contacts. */
  approveHeadshot(draftId: string): Promise<ApproveHeadshotResult>;
  /** List (or search, when `q` is given) the user's synced Google contacts. */
  getContacts(q?: string): Promise<ContactDTO[]>;
  /** ORBIT — the passive relationship profile for one contact (D-14), or null when none computed yet. */
  getRelationshipProfile(entityKey: string): Promise<RelationshipProfileDTO | null>;
  /** ORBIT — confirm the inferred introducer (D-5), optionally overriding the name. */
  confirmIntroducer(entityKey: string, name?: string): Promise<RelationshipProfileDTO>;
  /** ORBIT — dismiss the inferred introducer (D-5). */
  dismissIntroducer(entityKey: string): Promise<RelationshipProfileDTO>;
  /** People recently interacted with (email/calendar), newest first. */
  getRecentContacts(): Promise<ContactDTO[]>;
  /** Merge the given contacts into one canonical (unions their emails; survives re-sync). */
  mergeContacts(contactIds: string[]): Promise<{ canonicalId: string }>;
  /** Trigger an on-demand contacts pull + reconciliation; resolves with the cache size. */
  syncContacts(): Promise<{ contacts: number }>;
  /** Pending "link this mention to a contact" suggestions awaiting review. */
  getContactSuggestions(): Promise<ContactSuggestionDTO[]>;
  confirmContactSuggestion(suggestionId: string): Promise<void>;
  dismissContactSuggestion(suggestionId: string): Promise<void>;
  /** Delete a note server-side (registry + stored doc + backlink edges). Used to discard blank pages. */
  deleteNote(noteId: string): Promise<void>;
  /** Record a note open for the cross-device Recent list (stamps openedAt server-side). */
  openNote(noteId: string): Promise<void>;
  /** Remove a note from Recent everywhere (clears openedAt server-side). */
  dismissRecent(noteId: string): Promise<void>;
  /** Favorite/unfavorite a note for the cross-device Favorites list (sets starred server-side). */
  setStarred(noteId: string, starred: boolean): Promise<void>;
  /** Resolve a `[[name]]` to the note that is its page (IMPL-07). */
  resolvePage(name: string): Promise<PageResolutionDTO>;
  /** Linked References for a page: inbound edges grouped by source note. `name` is the page title. */
  getBacklinks(noteId: string, name?: string, opts?: BacklinksOpts): Promise<BacklinksDTO>;
  /** Re-derive a note's backlink edges from its stored snapshot (keeps the graph current). */
  reindexNote(noteId: string): Promise<void>;
  /** Resolve a block id to its note + text (for a `((id))` ref or block embed). */
  getBlock(blockId: string): Promise<BlockDTO | null>;
  /** Search blocks by text for the `((` picker. */
  searchBlocks(query: string): Promise<BlockDTO[]>;
  /** Full-content search over note bodies (RAG chunk text) for the Cmd+K palette. */
  searchContent(query: string, signal?: AbortSignal): Promise<ContentHitDTO[]>;
  /** LENS-03 — grouped cross-corpus palette results (People today). One call, per-corpus slices. */
  searchPalette(query: string, corpora: string[], signal?: AbortSignal): Promise<PaletteGroupDTO[]>;
  /** Read-only plain-text content of a page, for a page embed. */
  getPageContent(name: string): Promise<PageContentDTO | null>;
  /**
   * Route one natural-language line to an action (add task / add note / draft shipping label).
   * `dateKey` is the viewer's local day, so a saved note lands in today's journal; `signal` lets the
   * caller cancel an in-flight request (the ✦ Stop button).
   */
  assist(prompt: string, dateKey?: string, signal?: AbortSignal): Promise<AssistantResultDTO>;
  /**
   * OPENCLAW streaming — the same turn as {@link assist}, over SSE: `onStatus` fires once per tool the
   * agent executes (live progress for the palette), and the promise resolves with the final result.
   * Throws ApiError(404) against a pre-streaming server — callers fall back to {@link assist}.
   */
  assistStream(
    prompt: string,
    dateKey: string | undefined,
    onStatus: (ev: { type: string; tool?: string; round?: number }) => void,
    signal?: AbortSignal,
  ): Promise<AssistantResultDTO>;
  /**
   * REALTIME — hold the per-user signals SSE stream; `onWake` fires on every server-side signal
   * change (emit/resolve/dismiss from any service). Resolves when the server closes the stream
   * (Cloud Run request timeout — the caller reconnects); rejects on startup failure.
   */
  signalStream(onWake: (ev: { dateKey?: string }) => void, signal?: AbortSignal): Promise<void>;
  /**
   * REMIT-CHAT — upload a bill image to the assistant; returns a confirm-first draft (never auto-pays).
   * `prompt` is any text typed alongside the attachment, recorded as the user's words in the thread.
   */
  assistBillImage(bytes: Uint8Array, contentType: string, prompt?: string): Promise<AssistantResultDTO>;
  /** REMIT-CHAT — the confirm tap: pay a drafted bill (guard + worker). */
  confirmBillPay(billId: string): Promise<{ ok: boolean; status: string }>;
  /** REMIT-CHAT — one bill's live status, polled after the confirm tap so the Pay card resolves. */
  billStatus(billId: string): Promise<{ billId: string; status: string; lastError?: string }>;
  /** DOCKET — every bill record, newest first (server caps at 100). Powers the Bills tab. */
  listBills(): Promise<BillRecord[]>;
  /** DOCKET — one full bill record (the widened `/me/bills/{id}` response). */
  getBill(billId: string): Promise<BillRecord>;
  /** RELAY — every non-archived Job for the board (all lanes). */
  listJobs(): Promise<Job[]>;
  /** RELAY — one Job by id. */
  getJob(jobId: string): Promise<Job>;
  /** SENTRY — the user's watch rules (with English-rendered predicates). */
  listWatches(): Promise<Watch[]>;
  createWatch(input: WatchCreateInput): Promise<Watch>;
  setWatchEnabled(id: string, enabled: boolean): Promise<Watch>;
  deleteWatch(id: string): Promise<void>;
  /** DOCKET — one bill's payment-run history, newest first (sanitized steps + error/errorStack). */
  listBillRuns(billId: string): Promise<BillPayRunDTO[]>;
  /** DOCKET D5 — "I paid this": close a bill settled outside Lapo (full side-effect sync). */
  selfPaidBill(billId: string): Promise<{ ok: boolean; status: string }>;
  /** TEMPER T-D11 — teach & retry: store a hint verbatim and re-arm one attempt immediately. */
  teachBill(billId: string, hint: string): Promise<{ ok: boolean; status: string }>;
  /** DOCKET D18 — cancel an armed auto-pay window. */
  cancelAutoPayBill(billId: string): Promise<{ ok: boolean; status: string }>;
  /** TEMPER-05 — cancel a pending auto-retry (countdown card). */
  cancelRetryBill(billId: string): Promise<{ ok: boolean; status: string }>;
  /** TEMPER-07 (T-D9) — the biller dossier (shared typed facts) + the user's overlay for a bill. */
  getBillKnowledge(billId: string): Promise<{ dossier: BillerDossier | null; overlay: BillerOverlay | null }>;
  /** DOCKET D7 — upload a bill photo straight into the extraction pipeline. */
  importBillPhoto(bytes: Uint8Array, contentType: string): Promise<{ isBill: boolean; billId?: string }>;
  /** DOCKET round 2 — archive/unarchive a bill (hidden behind the list filter; forensics kept). */
  setBillArchived(billId: string, archived: boolean): Promise<{ ok: boolean; archived: boolean }>;
  /** DOCKET round 2 — hard-delete a bill and its run history. */
  deleteBill(billId: string): Promise<{ ok: boolean }>;
  /** The saved ✦ conversation (server-side memory), oldest first — used to rehydrate the palette. */
  assistThread(): Promise<AssistantTurnDTO[]>;
  /** End the ✦ conversation server-side (the sweep / "clear") so the next line starts fresh. */
  assistClear(): Promise<void>;
  /** DIAL: live state of one placed voice call — polled until terminal so the outcome lands in-thread. */
  voiceCallStatus(callId: string): Promise<VoiceCallStatusDTO>;
  /** RELAY: Telegram connection status for the "Connect Telegram" card. `configured:false` when the
   * product bot isn't provisioned yet. */
  telegramStatus(): Promise<{ configured: boolean; linked: boolean; username?: string }>;
  /** RELAY: mint a one-time link code + t.me deep link the user sends to the bot to pair this chat. */
  telegramLinkCode(): Promise<{ code: string; url: string; expiresAt: number }>;
  /** RELAY: disconnect the user's linked Telegram chat. */
  telegramUnlink(): Promise<{ ok: boolean }>;
  /** The Daily Signals feed for one day. `today` (the viewer's local day) makes roll-forward TZ-safe. */
  getSignals(dateKey: string, today?: string): Promise<SignalDTO[]>;
  /** The historical signals log across a window of days (inclusive `YYYY-MM-DD` bounds). */
  getSignalsRange(from: string, to: string): Promise<SignalDTO[]>;
  /** Mark a signal seen (clears its unread affordance; measured once). */
  markSignalSeen(signalId: string): Promise<SignalDTO | null>;
  /** Dismiss a signal. */
  dismissSignal(signalId: string): Promise<SignalDTO | null>;
  /**
   * Set a signal aside (HONE) — the neutral "✕ / I'm not sure". Hides the card WITHOUT judging its
   * relevance, so (unlike {@link dismissSignal}) it never feeds the noise-tuning loop.
   */
  setAsideSignal(signalId: string): Promise<SignalDTO | null>;
  /** HONE — spawn an artifact from a signal (⋯ Create). `opts.number` is the owner-confirmed E.164 for a call. */
  createSignalArtifact(signalId: string, kind: CreateActionKind, opts?: { number?: string }): Promise<SignalCreateArtifactResultDTO | null>;
  /** HONE — resolve candidate numbers for a ⋯ Create → Call (contacts → Google Maps → web). */
  resolveCallNumber(
    signalId: string,
  ): Promise<{ targetName: string; candidates: Array<{ number: string; source: 'contacts' | 'maps' | 'web'; label: string }> }>;
  /** HONE — remove an artifact created from a signal (⋯ Remove): runs the inverse op, drops the link. */
  removeSignalArtifact(signalId: string, artifactId: string): Promise<SignalDTO | null>;
  /** Snooze a signal forward by `days`; it resurfaces on the new day. */
  snoozeSignal(signalId: string, days: number): Promise<SignalDTO | null>;
  /**
   * Act on a signal's action (runs create-task/create-note/create-draft-reply/archive/rsvp… server-side).
   * `editedBody` uses an owner-edited draft (spec A8); `actionKind` runs a non-primary action (e.g. RSVP
   * Decline). Returns the created artifact ref, or `relinkRequired` when the account needs re-linking.
   */
  actSignal(signalId: string, opts?: { editedBody?: string; actionIndex?: number }): Promise<SignalActResultDTO | null>;
  /** Reject a proposed/escalated action (records a `rejected` outcome for the learning loop — spec A15). */
  rejectSignal(signalId: string): Promise<SignalDTO | null>;
  /**
   * Mark a triage card as spam (SPAM-*): archives + marks-read the Gmail message and teaches the spam
   * learner so the sender-identity is auto-handled from now on. Returns `relinkRequired` when the account
   * still needs the gmail.modify scope (same re-link path as `actSignal`).
   */
  markSpamSignal(signalId: string): Promise<SignalActResultDTO | null>;
  /**
   * Mark a triage card as coming from an automated sender (the "Automated" button): teaches the classifier
   * so its future mail is handled quietly (audit line / high-stakes task only) and clears the card. Softer
   * than spam — takes no Gmail action, so it never needs a re-link.
   */
  markAutomatedSignal(signalId: string): Promise<SignalDTO | null>;
  /**
   * Request an on-demand reply draft for an email card. Records the positive learning signal, enqueues the
   * (slow, model-bound) generation on the worker, and clears this card; a "Review draft" card arrives when
   * ready. Non-blocking — returns as soon as the work is queued.
   */
  draftReply(signalId: string, opts: { accountId: string; messageId: string; sender?: string }): Promise<void>;
  /**
   * Review-draft dialog — fetch the SOURCE email behind a draft-reply card (the scrollable pane). `null`
   * when the message is gone or the card carries no email ids (the dialog then shows just the draft).
   */
  getSignalSourceEmail(signalId: string): Promise<SignalSourceEmailDTO | null>;
  /**
   * Review-draft dialog — "Teach → iterate": re-run the reply model with the owner's instruction, persist
   * the new body onto the card, and return it so the dialog updates in place. `null` on failure.
   */
  regenerateDraft(signalId: string, opts: { guidance: string }): Promise<{ draftBody: string } | null>;
  /**
   * Review-draft dialog — "Send email": SEND the reply under the thread (the client fires this only after
   * its undo window elapses), then the card resolves. Returns `relinkRequired` when the account lacks the
   * compose scope (same re-link path as `actSignal`).
   */
  sendSignalReply(
    signalId: string,
    opts: { body: string; archive?: boolean },
  ): Promise<{ ok?: boolean; relinkRequired?: string; archiveRelink?: string } | null>;
  /** Clear the whole triage feed — dismisses every proposal/escalation/audit card. Returns how many cleared. */
  resetTriage(): Promise<number>;
  /** SETTLE-03 — on-read liveness pass: clear cards whose source was already handled (best-effort). */
  reconcileSignals(): Promise<{ checked: number; cleared: number }>;
  // ── ECHO — weekly note resurfacing (note-resurfacing.md) ─────────────────────────────────────────
  /** Per-item "Not relevant" on the resurface digest: 180-day suppression, card lifecycle untouched. */
  resurfaceNotRelevant(targetId: string): Promise<void>;
  /** The resurface opt-in flag (D8). */
  getResurfaceConfig(): Promise<{ enabled: boolean }>;
  setResurfaceConfig(enabled: boolean): Promise<void>;
  /** Run this user's weekly resurface now (dogfood/Run-now; idempotent within the ISO week — D10). */
  runResurface(): Promise<{ candidates: number; items: number; emitted: boolean; skipped: boolean }>;
  // ── SIEVE — ingest triage (ingest-triage.md) ─────────────────────────────────────────────────────
  /** The recoverable filtered-items list (S6/S8). */
  listParkedIngest(): Promise<ParkedIngest[]>;
  /** One-tap restore: re-runs the retained record through its original pipeline (S7). */
  restoreParkedIngest(parkedId: string): Promise<boolean>;
  /** Precision proxy over enforced parks (S11). */
  getIngestTriageScorecard(): Promise<{ parks: number; restores: number; precision: number }>;
  // ── TUNE — unified feedback + guidance (signal-feedback.md) ──────────────────────────────────────
  /** The uniform capture (U1): relevance / action-quality / teach-text on any card. */
  recordSignalFeedback(signalId: string, input: { relevance?: 'useful' | 'not-useful'; actionQuality?: 'good' | 'not-good'; note?: string }): Promise<boolean>;
  // ── HELM P1-8 — the morning brief (C9) ─────────────────────────────────────────────────────────
  /** The morning brief for a day: rendered items + the ledger `deliveryId` the thumbs patch. */
  getBrief(dateKey: string): Promise<MorningBriefDTO | null>;
  /** HELM P2-1 — land a brief-thumb reaction (useful/noise/wrong); a `wrong` tap carries the four-way `wrongCause` (DL-21). */
  recordBriefReaction(
    deliveryId: string,
    reaction: 'useful' | 'noise' | 'wrong',
    wrongCause?: 'wrong-fact' | 'already-handled' | 'duplicate' | 'bad-timing',
  ): Promise<boolean>;
  /**
   * HONE — the global "Teach" affordance: a freeform note tied to a signal (`signalId`) OR to a view
   * (`producerId`). Available everywhere; routes to the same nightly distiller.
   */
  teach(input: { note: string; signalId?: string; producerId?: ProducerId; entityRef?: string }): Promise<boolean>;
  /** Quality vote on an auto-executed recap row (U8). */
  recordAutonomyFeedback(eventId: string, quality: 'good' | 'not-good', note?: string): Promise<boolean>;
  /** Per-kind usefulness over the trailing 30d (U10). */
  getFeedbackScores(): Promise<KindUsefulness[]>;
  /** The Guidance Ledger (U6): list / manual add + edit / delete / approve-proposed. */
  listGuidance(): Promise<GuidanceEntry[]>;
  putGuidance(input: { entryId?: string; producerId: ProducerId; kind: 'rule' | 'fewshot'; text: string }): Promise<GuidanceEntry>;
  deleteGuidance(entryId: string): Promise<void>;
  /** HELM P2-2 — accept a proposed entry, optionally replacing its line (edit-on-approve). */
  approveGuidance(entryId: string, text?: string): Promise<void>;
  /** HELM P2-2/P2-4 — reject a proposed entry (the human gate; DL-16). Suppresses re-proposal 30d. */
  rejectGuidance(entryId: string): Promise<void>;
  /** HELM P1-4/P2-4 — the delivery ledger for the last `days` days (default 7), for the review scorecard. */
  getAttentionLedger(days?: number): Promise<DeliveryRecord[]>;
  /** MARROW — the assistant's durable memory: list / add (manual) / edit / delete. */
  listAssistantMemories(): Promise<AssistantMemoryDTO[]>;
  createAssistantMemory(input: { text: string; kind?: AssistantMemoryDTO['kind'] }): Promise<AssistantMemoryDTO>;
  updateAssistantMemory(
    memoryId: string,
    patch: { text?: string; kind?: AssistantMemoryDTO['kind']; disabled?: boolean },
  ): Promise<AssistantMemoryDTO>;
  deleteAssistantMemory(memoryId: string): Promise<void>;
  // ── HELM P3-6 — the USER.md / RULES.md panes (MARROW record-list replacement) ─────────────────────
  /** USER.md — the pinned `preference|fact|project` memory records. */
  listPinnedMemory(): Promise<AssistantMemoryDTO[]>;
  /** RULES.md — every rule (active + superseded, so a retired line can render struck-through, DL-17). */
  listRules(): Promise<RuleDTO[]>;
  /** Edit a RULES.md line: writes a NEW dated line and supersedes the old (kept + marked, never deleted). */
  editRule(ruleId: string, text: string): Promise<RuleDTO>;
  /** Retire a RULES.md line: mark it superseded (kept), never a hard delete (DL-17). */
  retireRule(ruleId: string): Promise<void>;
  /** The "why?" provenance chain for one ref (a subjectKey, ruleId, or memoryId). READ-ONLY. */
  explain(ref: string): Promise<ExplainResultDTO>;
  /** Read per-kind engagement metrics (spec D18). */
  getSignalMetrics(): Promise<SignalMetrics[]>;
  // ── Disposition trust ladder + learning loop (ACT-06) ────────────────────────────────────────────
  /** The accreted per-category policy rules (Settings rule table). */
  getDispositionPolicy(): Promise<DispositionPolicyEntryDTO[]>;
  /** Write a per-category rule — the inline quick-tune buttons + the Settings table (spec A7/A22). */
  setDispositionPolicy(category: string, policy: DispositionPolicyEntryDTO['policy']): Promise<void>;
  /** Remove a rule, reverting the category to the confirm default. */
  removeDispositionPolicy(category: string): Promise<void>;
  /** The pending auto-graduation proposals (spec A16). */
  getDispositionProposals(): Promise<DispositionProposalDTO[]>;
  /** Accept a graduation proposal → the category goes to `auto`. */
  acceptDispositionProposal(category: string): Promise<void>;
  /** The inspectable "what Lapo has learned" aggregate (spec A15). */
  getDispositionFeedback(): Promise<DispositionLearnedDTO[]>;
  /** Reset the learning for one key, or all of it (spec A15). */
  clearDispositionFeedback(key?: string): Promise<void>;
  // ── AXIS — earned autonomy: trust rules, routing rules, proposals, recap, scorecard, undo ────────
  /** Master-flag + rule-count status (X22). The flag itself is set via updateProfile({axisAutonomy}). */
  getAutonomyStatus(): Promise<{ enabled: boolean; rules: number }>;
  /** X7 — run the one-shot cold-start analyzer over past Gmail behavior (proposals only, never applied). */
  runColdStart(): Promise<{ proposals: string[]; fewShotSeeds: number; scannedMessages: number }>;
  /** The trust-rule ledger (X14): every learned/owner-set rule with policy + streak/strike state. */
  listTrustRules(): Promise<TrustRuleDTO[]>;
  /** Write a trust rule (the card ⋯ standing-rule shortcuts + the ledger dropdown — X-05/X14/X27). */
  setTrustRule(ruleKey: string, policy: TrustRuleDTO['policy'], target?: TrustRuleTargetDTO): Promise<TrustRuleDTO | null>;
  /** Delete a trust rule. */
  removeTrustRule(ruleKey: string): Promise<void>;
  /** Reset a rule's learned state (streak/strikes) without changing its policy (X14). */
  resetTrustRule(ruleKey: string): Promise<void>;
  /**
   * Accept a graduation proposal — the rule goes `auto` (X18, the owner's explicit yes). HELM P4-3:
   * the owner's `once | subject | always` choice is sent as `scope` and the server writes a scoped
   * `Grant` in addition to the TrustRule flip (P4-2 wired the server side). Omitting `scope` keeps the
   * pre-P4-2 default (`always`).
   */
  acceptGraduation(ruleKey: string, scope?: GrantScopeChoice): Promise<void>;
  /** Decline a graduation proposal — cool-off before Lapo re-proposes (X18). */
  declineGraduation(ruleKey: string): Promise<void>;
  // ── HELM P4-3 — scoped grants (the Autonomy module's live-grants list + revoke) ──────────────────
  /** The live standing grants (non-revoked, non-expired, subject-resolve-elided; DL-22). */
  listGrants(): Promise<GrantDTO[]>;
  /**
   * Create a grant. The server rejects any §5-floored verb (send/pay) — `GrantActionKind` excludes them
   * at the type level (DL-13), so only the non-floored verbs are callable. `subject` scope needs a key.
   */
  createGrant(input: {
    actionKind: GrantActionKind;
    scope: GrantScopeChoice;
    subjectKey?: string;
    expiresOnResolve?: true;
  }): Promise<GrantDTO>;
  /** Revoke a grant — one-tap-reversible autonomy (DL-17). Idempotent. */
  revokeGrant(grantId: string): Promise<void>;
  /** The routing-rule ledger (X26): loudness / priority / batch / snooze / TTL per kind/sender. */
  listRoutingRules(): Promise<RoutingRuleDTO[]>;
  /** Write (merge) a routing rule (the card ⋯ "This signal" group + the ledger Routing tab). */
  setRoutingRule(rule: Partial<RoutingRuleDTO> & { ruleKey: string }): Promise<RoutingRuleDTO>;
  /** Delete a routing rule. */
  removeRoutingRule(ruleKey: string): Promise<void>;
  /** The day's auto-executed actions (the recap list — X4). */
  getAutonomyRecap(dateKey?: string): Promise<AutonomyEventDTO[]>;
  /** The composite scorecard days (X19). Defaults to the trailing 30 days. */
  getAutonomyScorecard(from?: string, to?: string): Promise<AutonomyScorecardDayDTO[]>;
  /** Undo one auto-action (X4; records a correction → strike X5). */
  undoAutonomyEvent(eventId: string): Promise<boolean>;
  /** Bulk-undo a day's recap (X4). */
  undoAutonomyDay(dateKey: string): Promise<{ undone: number; failed: number }>;
  /** Read the user's saved ship-from (return) address (seeded default if unset). */
  getShipFrom(): Promise<ShipAddressDTO>;
  /** Persist the user's ship-from address; the server coerces before storing. */
  putShipFrom(address: ShipAddressDTO): Promise<ShipAddressDTO>;
  /** Draft + price a shipment from a natural-language prompt (LLM parse; no charge). */
  draftShipment(prompt: string): Promise<DraftResultDTO>;
  /** Buy a label for a drafted shipment (a charge). `rateId` defaults to the cheapest rate. */
  buyLabel(easypostId: string, rateId?: string): Promise<ShipmentDTO>;
  /** List the user's previously bought labels, newest-first. */
  listShipments(): Promise<ShipmentDTO[]>;
  /** Import recent purchased labels from the connected EasyPost account (deduped). */
  backfillShipments(limit?: number): Promise<{ imported: number; shipments: ShipmentDTO[] }>;
  /** Pull the latest labels from EasyPost AND refresh every stored label's delivery status in one call. */
  syncShipments(limit?: number): Promise<{ imported: number; refreshed: number; shipments: ShipmentDTO[] }>;
  /** Refresh one shipment's delivery status from EasyPost. */
  refreshShipment(shipmentId: string): Promise<ShipmentDTO>;
  /** Edit a label's title (label) and/or description (note); a blank value clears the field. */
  updateShipmentDetails(shipmentId: string, patch: { label?: string; note?: string }): Promise<ShipmentDTO>;
  /** Archive (hide from the default list) or restore a bought label. */
  archiveShipment(shipmentId: string, archived: boolean): Promise<ShipmentDTO>;
  /** Track an arbitrary package by tracking number (carrier optional → EasyPost auto-detects). */
  trackPackage(trackingCode: string, carrier?: string, label?: string): Promise<TrackedPackageDTO>;
  /** List the user's tracked packages, newest-first. */
  listTracked(): Promise<TrackedPackageDTO[]>;
  /** Scan every linked Gmail mailbox for recent shipment mail and start tracking any codes found. */
  scanEmailForShipments(): Promise<{ mailboxes: number; scanned: number; tracked: number }>;
  /** Refresh one tracked package's status from EasyPost. */
  refreshTracked(trackedId: string): Promise<TrackedPackageDTO>;
  /** Edit a tracked package's title (label) and/or description (item); a blank value clears the field. */
  updateTrackedDetails(trackedId: string, patch: { label?: string; item?: string }): Promise<TrackedPackageDTO>;
  /** Stop tracking a package. */
  /** Archive (hide from the default list) or restore a tracked package. */
  archiveTracked(trackedId: string, archived: boolean): Promise<TrackedPackageDTO>;
  untrack(trackedId: string): Promise<void>;
  /** OAuth consent screen: describe the requesting app + scopes (validates client + redirect_uri). */
  getOAuthConsent(clientId: string, redirectUri: string, scope: string): Promise<OAuthConsentInfo>;
  /** Approve an OAuth authorization → mint a code; returns the redirect URL to send the browser to. */
  approveOAuth(input: OAuthAuthorizeInput): Promise<{ redirect: string; scopes: string[] }>;
  /** Settings → Authorized apps: list this user's active third-party grants. */
  listAuthorizedApps(): Promise<AuthorizedAppDTO[]>;
  /** Revoke a third-party authorization by grant id. */
  revokeAuthorizedApp(grantId: string): Promise<void>;

  // --- Decision Ledger (LEDGER-06) ---
  /** Faceted + full-text list of decisions (D17), newest day first. */
  getDecisions(query?: DecisionQuery): Promise<DecisionRecord[]>;
  /** One decision + resolved initiative names + supersession summaries + source deep-links (D15). */
  getDecision(decisionId: string): Promise<DecisionDetailDTO>;
  /** Manually log a decision (D22) — origin:'manual', runs the resolvers + FTS. */
  createDecision(input: ManualDecisionInput): Promise<DecisionRecord>;
  /** Owner edit (D7/D20); a low→high confidence flip records a confirm-flagged correction. */
  patchDecision(decisionId: string, fields: Partial<Pick<DecisionRecord, 'summary' | 'detail' | 'note' | 'categories' | 'confidence' | 'confidenceScore' | 'revisitAt' | 'status'>>): Promise<DecisionRecord>;
  /** Delete a decision — tombstones it (D19). `reason:'reject-flagged'` for a flagged-row dismiss. */
  deleteDecision(
    decisionId: string,
    reason?: 'delete' | 'reject-flagged',
    correctionReason?: DecisionCorrectionReason,
  ): Promise<void>;
  /** Merge losers into a keeper — losing dedupeKeys become aliases (D20). */
  mergeDecisions(keepId: string, mergeIds: string[]): Promise<DecisionRecord>;
  /** Set status + supersession link (D10). */
  setDecisionStatus(decisionId: string, status: DecisionStatus, supersedesId?: string): Promise<DecisionRecord>;
  /** Learned + manual rules (D9). */
  getDecisionRules(): Promise<DecisionRule[]>;
  upsertDecisionRule(rule: { ruleId?: string; kind: DecisionRuleKind; selector: string; replacement?: string; enabled?: boolean }): Promise<DecisionRule>;
  setDecisionRuleEnabled(ruleId: string, enabled: boolean): Promise<DecisionRule>;
  deleteDecisionRule(ruleId: string): Promise<void>;
  /** Per-user config (Enh-C): enable toggle + tunable confidence flag boundary. */
  getDecisionLedgerConfig(): Promise<DecisionLedgerConfig>;
  putDecisionLedgerConfig(patch: { enabled?: boolean; confidenceThreshold?: number }): Promise<DecisionLedgerConfig>;
  /** Owner "Run now" (today) + "Backfill N days" from the integration settings. */
  runDecisionLedger(): Promise<{ created: number; merged: number; superseded: number }>;
  backfillDecisionLedger(days: number): Promise<{ processed: number; created: number }>;

  // --- Meeting-note ↔ calendar-event binding ---
  /** The note bound to a calendar event, or null. */
  noteForEvent(eventId: string): Promise<string | null>;
  /** Bind a note to a calendar event (meeting note); pass null to unbind. */
  bindNoteToEvent(noteId: string, eventId: string | null): Promise<void>;

  // --- LOOM (initiative×quarter matrix) ---
  loom: LoomApi;
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Returns a fresh Identity Platform ID token, or null when signed out. */
  getToken: () => Promise<string | null>;
  /** Injectable for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

/**
 * True when the browser already knows there is no network (airplane mode, dead Wi-Fi). Per the HTML
 * spec `navigator.onLine === false` means the UA is *definitely* offline (the unreliable direction is
 * the opposite — `true` doesn't guarantee reachability), so acting on `false` is safe: we reject
 * instantly instead of dialing a socket that can't connect and letting the OS TCP stack stall for
 * minutes. Guards non-browser (test/SSR) envs where `navigator` is undefined.
 *
 * Deliberately NOT a request timeout: several endpoints (AI assist, vision, uploads, server-side
 * backfills) legitimately run far longer than any timeout we'd pick, so a blanket abort would cut off
 * working requests. When the browser knows it's online we let the request run to completion exactly as
 * before; the offline-first cache (see App.tsx) is what keeps the UI responsive meanwhile.
 */
export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

export function createApiClient({ baseUrl, getToken, fetchImpl = fetch }: ApiClientOptions): ApiClient {
  const base = baseUrl.replace(/\/$/, '');

  /**
   * fetchImpl with a single offline guard: reject before dialing when the device is known-offline.
   * Otherwise it's a straight passthrough — the caller's `init` (including any AbortSignal for
   * search/assist cancellation) reaches fetch unchanged, so request timing/cancellation behaves
   * exactly as it did before this guard existed.
   */
  async function guardedFetch(input: string, init: RequestInit = {}): Promise<Response> {
    if (isOffline()) throw new ApiError(0, 'offline');
    return fetchImpl(input, init);
  }

  async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
    // Short-circuit before getToken(): Firebase's getIdToken() refreshes an expired token over the
    // network, so it too can hang while offline.
    if (isOffline()) throw new ApiError(0, 'offline');
    const token = await getToken();
    if (!token) throw new ApiError(401, 'not signed in');
    const res = await guardedFetch(`${base}${path}`, {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        authorization: `Bearer ${token}`,
        'content-type': 'application/json',
      },
    });
    if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
    return (await res.json()) as T;
  }

  /**
   * Upload raw bytes to POST /files. Unlike {@link call} this sends the file body with its own
   * content-type (not application/json) and the filename in a header, matching the server's
   * header-metadata upload contract (no multipart parsing).
   */
  async function uploadFile(file: File): Promise<UploadedBlobDTO> {
    if (isOffline()) throw new ApiError(0, 'offline');
    const token = await getToken();
    if (!token) throw new ApiError(401, 'not signed in');
    const res = await guardedFetch(`${base}/files`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': file.type || 'application/octet-stream',
        'x-filename': encodeURIComponent(file.name),
      },
      body: file,
    });
    if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
    return (await res.json()) as UploadedBlobDTO;
  }

  /** The server sends errors as `{ error: "…" }`; surface that human text, falling back to the raw body. */
  async function errorMessage(res: Response): Promise<string> {
    const body = await res.text().catch(() => '');
    if (body) {
      try {
        const parsed = JSON.parse(body) as { error?: unknown };
        if (typeof parsed.error === 'string' && parsed.error) return parsed.error;
      } catch {
        return body;
      }
    }
    return body || res.statusText;
  }

  return {
    getMe: () => call<MeResponse>('/me'),
    getHealth: () => call<HealthSnapshot>('/health'),
    getHealthEvents: (limit) => call<{ events: HealthEventDTO[] }>(`/admin/health/events${limit ? `?limit=${limit}` : ''}`),
    getAdminLogs: (opts) => {
      const q = new URLSearchParams();
      if (opts?.severity) q.set('severity', opts.severity);
      if (opts?.windowMs) q.set('windowMs', String(opts.windowMs));
      if (opts?.pageSize) q.set('pageSize', String(opts.pageSize));
      const qs = q.toString();
      return call<{ entries: RawLogEntry[] }>(`/admin/logs${qs ? `?${qs}` : ''}`);
    },
    updateProfile: (patch) => call<MeResponse>('/me/profile', { method: 'PUT', body: JSON.stringify(patch) }),
    bootstrapWritingStyle: () =>
      call<{ profile: WritingStyleProfile; sampledMessages: number; me: MeResponse }>('/me/writing-style/bootstrap', { method: 'POST' }),
    getShareToken: () => call<{ token: string }>('/me/share-token', { method: 'POST' }),
    beginLink: (provider, scopes = []) =>
      call<OAuthLinkStart>('/accounts/link', { method: 'POST', body: JSON.stringify({ provider, scopes }) }),
    completeLink: (state, code) =>
      call<LinkedAccountDTO>('/accounts/link/complete', { method: 'POST', body: JSON.stringify({ state, code }) }),
    startLinkBroker: (scopes = []) =>
      call<{ authUrl: string; sessionId: string }>('/accounts/link/broker/start', { method: 'POST', body: JSON.stringify({ scopes }) }),
    startSlackLinkBroker: () =>
      call<{ authUrl: string; sessionId: string }>('/accounts/slack/link/broker/start', { method: 'POST', body: JSON.stringify({}) }),
    startTodoistLinkBroker: () =>
      call<{ authUrl: string; sessionId: string }>('/accounts/todoist/link/broker/start', { method: 'POST', body: JSON.stringify({}) }),
    pollLinkBroker: (sessionId) =>
      call<{ status: 'pending' | 'done' | 'error' | 'unknown'; accountId?: string; error?: string }>(
        `/accounts/link/broker/poll?session=${encodeURIComponent(sessionId)}`,
      ),
    beginTodoistLink: () => call<OAuthLinkStart>('/accounts/todoist/link', { method: 'POST' }),
    completeTodoistLink: (state, code) =>
      call<LinkedAccountDTO>('/accounts/todoist/link/complete', {
        method: 'POST',
        body: JSON.stringify({ state, code }),
      }),
    beginTessetaLink: () => call<OAuthLinkStart>('/accounts/tesseta/link', { method: 'POST' }),
    completeTessetaLink: (state, code) =>
      call<LinkedAccountDTO>('/accounts/tesseta/link/complete', {
        method: 'POST',
        body: JSON.stringify({ state, code }),
      }),
    linkEasyPost: (apiKey) =>
      call<LinkedAccountDTO>('/accounts/easypost', { method: 'POST', body: JSON.stringify({ apiKey }) }),
    linkClaude: (apiKey) =>
      call<LinkedAccountDTO>('/accounts/claude', { method: 'POST', body: JSON.stringify({ apiKey }) }),
    linkGranola: (apiKey) =>
      call<LinkedAccountDTO>('/accounts/granola', { method: 'POST', body: JSON.stringify({ apiKey }) }),
    getGranolaConfig: () => call<GranolaConfig>('/me/granola-config'),
    putGranolaConfig: (config) =>
      call<GranolaConfig>('/me/granola-config', { method: 'PUT', body: JSON.stringify(config) }),
    startSlackLink: () => call<OAuthLinkStart>('/accounts/slack/link/start', { method: 'POST' }),
    completeSlackLink: (input) =>
      call<LinkedAccountDTO>('/accounts/slack/link/complete', { method: 'POST', body: JSON.stringify(input) }),
    listSlackWorkspaces: () => call<{ workspaces: SlackWorkspaceView[] }>('/me/slack/workspaces'),
    unlinkSlackWorkspace: (accountId, teamId) =>
      call<void>('/accounts/slack', { method: 'DELETE', body: JSON.stringify({ accountId, teamId }) }),
    getSlackConfig: () => call<SlackConfig>('/me/slack-config'),
    putSlackConfig: (config) =>
      call<SlackConfig>('/me/slack-config', { method: 'PUT', body: JSON.stringify(config) }),
    putSlackWorkspace: (teamId, config) =>
      call<unknown>(`/me/slack/workspace/${encodeURIComponent(teamId)}`, { method: 'PUT', body: JSON.stringify(config) }),
    postClientError: (report) =>
      call<{ ok: boolean }>('/client-errors', { method: 'POST', body: JSON.stringify(report) }),
    runSlackDigest: () => call<SlackRunResultDTO>('/integrations/slack/run', { method: 'POST' }),
    draftSlackReply: (input) =>
      call<{ text: string }>('/slack/reply/draft', { method: 'POST', body: JSON.stringify(input) }),
    sendSlackReply: (input) =>
      call<{ ts: string }>('/slack/reply/send', { method: 'POST', body: JSON.stringify(input) }),
    getGeminiConfig: () => call<GeminiConfig>('/me/gemini-config'),
    putGeminiConfig: (config) =>
      call<GeminiConfig>('/me/gemini-config', { method: 'PUT', body: JSON.stringify(config) }),
    getTodoistProjects: () =>
      call<{ linked: boolean; projects: TodoistProject[]; error?: string; needsReconnect?: boolean }>(
        '/me/todoist/projects',
      ),
    getMovieEnricherConfig: () => call<MovieEnricherConfig>('/me/movie-enricher-config'),
    putMovieEnricherConfig: (config) =>
      call<MovieEnricherConfig>('/me/movie-enricher-config', { method: 'PUT', body: JSON.stringify(config) }),
    getRestaurantVerifierConfig: () => call<RestaurantVerifierConfig>('/me/restaurant-verifier-config'),
    putRestaurantVerifierConfig: (config) =>
      call<RestaurantVerifierConfig>('/me/restaurant-verifier-config', { method: 'PUT', body: JSON.stringify(config) }),
    getInstagramShareConfig: () => call<InstagramShareConfig>('/me/instagram-config'),
    putInstagramShareConfig: (config) =>
      call<InstagramShareConfig>('/me/instagram-config', { method: 'PUT', body: JSON.stringify(config) }),
    getPodcastConfig: () => call<PodcastConfig>('/me/podcasts-config'),
    putPodcastConfig: (config) => call<PodcastConfig>('/me/podcasts-config', { method: 'PUT', body: JSON.stringify(config) }),
    runPodcasts: (req) => call<PodcastRunView>('/me/podcasts/run', { method: 'POST', body: JSON.stringify(req) }),
    listPodcastRuns: (limit = 20) => call<{ runs: PodcastRunView[] }>(`/me/podcasts/runs?limit=${limit}`),
    slashPodcast: (link, opts) =>
      call<{ runId: string; placeholder?: { noteId: string; sectionId: string } }>('/me/podcasts/slash', {
        method: 'POST',
        body: JSON.stringify({ link, dateKey: opts?.dateKey, itemId: opts?.itemId }),
      }),
    getVoiceConfig: () => call<VoiceConfig>('/me/voice-config'),
    putVoiceConfig: (config) =>
      call<VoiceConfig & { webhookConfigured?: boolean; webhookError?: string }>('/me/voice-config', { method: 'PUT', body: JSON.stringify(config) }),
    getVoiceResources: () => call<VoiceResources>('/me/voice/resources'),
    getBillPayConfig: () => call<BillPayConfig>('/me/billpay-config'),
    putBillPayConfig: (config) => call<BillPayConfig>('/me/billpay-config', { method: 'PUT', body: JSON.stringify(config) }),
    deletePaymentMethod: (cardId) => call<BillPayConfig>(`/me/payment-methods/${encodeURIComponent(cardId)}`, { method: 'DELETE' }),
    deleteBankAccount: (bankId) => call<BillPayConfig>(`/me/bank-accounts/${encodeURIComponent(bankId)}`, { method: 'DELETE' }),
    getRedditConfig: () => call<RedditConfig>('/me/reddit-config'),
    putRedditConfig: (config) =>
      call<RedditConfig>('/me/reddit-config', { method: 'PUT', body: JSON.stringify(config) }),
    getRedditResults: () => call<RedditResults>('/me/reddit/results'),
    postInstagramImport: (url, date, opts) =>
      call<InstagramImportResult>('/me/instagram-import', {
        method: 'POST',
        body: JSON.stringify({ url, date, ...opts }),
      }),
    rescanInstagramImport: (importId) =>
      call<InstagramImportResult>(`/me/instagram-import/${encodeURIComponent(importId)}/rescan`, { method: 'POST' }),
    resolveLinkMetadata: (url) =>
      call<{ bookmark: BookmarkMeta | null }>('/me/link-metadata', {
        method: 'POST',
        body: JSON.stringify({ url }),
      }).then((r) => r.bookmark),
    unlink: (accountId) =>
      call<{ ok: boolean }>('/accounts/unlink', { method: 'POST', body: JSON.stringify({ accountId }) }).then(() => undefined),
    registerDevice: (deviceId, token, platform = 'android') =>
      call<{ deviceId: string }>('/devices', {
        method: 'POST',
        body: JSON.stringify({ deviceId, token, platform }),
      }).then(() => undefined),
    unregisterDevice: (deviceId) =>
      call<void>(`/devices?deviceId=${encodeURIComponent(deviceId)}`, { method: 'DELETE' }).then(() => undefined),
    getTodoistToday: (date) =>
      call<TodoistTodayDTO>(`/integrations/todoist/today?date=${encodeURIComponent(date)}`),
    completeTodoistTask: (accountId, taskId) =>
      call<{ ok: boolean; needsReconnect?: boolean }>(
        `/integrations/todoist/tasks/${encodeURIComponent(taskId)}/complete`,
        {
          method: 'POST',
          body: JSON.stringify({ accountId }),
        },
      ),
    getAgendaConfig: () => call<AgendaSettings>('/me/agenda-config'),
    putAgendaConfig: (settings) =>
      call<AgendaSettings>('/me/agenda-config', { method: 'PUT', body: JSON.stringify(settings) }),
    telegramStatus: () => call<{ configured: boolean; linked: boolean; username?: string }>('/me/telegram'),
    telegramLinkCode: () => call<{ code: string; url: string; expiresAt: number }>('/me/telegram/link-code', { method: 'POST' }),
    telegramUnlink: () => call<{ ok: boolean }>('/me/telegram', { method: 'DELETE' }),
    getToolbarConfig: () => call<ToolbarConfig>('/me/toolbar-config'),
    putToolbarConfig: (config) =>
      call<ToolbarConfig>('/me/toolbar-config', { method: 'PUT', body: JSON.stringify(config) }),
    getCalendars: () => call<{ calendars: UserCalendarDTO[] }>('/me/calendars'),
    getCalendarPrefs: () => call<CalendarPrefs>('/me/calendar-prefs'),
    putCalendarPrefs: (prefs) =>
      call<CalendarPrefs>('/me/calendar-prefs', { method: 'PUT', body: JSON.stringify(prefs) }),
    getEngineCatalog: () => call<{ engines: EngineCatalogEntry[] }>('/engines/catalog'),
    getEngineConfig: () => call<EngineConfig>('/me/engine-config'),
    putEngineConfig: (config) =>
      call<EngineConfig>('/me/engine-config', { method: 'PUT', body: JSON.stringify(config) }),
    getAgendaDay: (dateKey) => {
      // Send the viewer's live browser timezone so the day window is bounded to their local day
      // (events near midnight otherwise bleed in from the adjacent day). Server falls back to the
      // user's configured agenda zone if this is ever unavailable.
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const q = `date=${encodeURIComponent(dateKey)}${tz ? `&tz=${encodeURIComponent(tz)}` : ''}`;
      return call<AgendaDayDTO>(`/agenda/events?${q}`);
    },
    listIntegrations: () =>
      call<{ integrations: HubIntegration[] }>('/me/integrations').then((r) => r.integrations),
    getCapabilities: () =>
      call<{ capabilities: CapabilityManifest }>('/me/capabilities').then((r) => r.capabilities),
    getIntegration: (id) =>
      call<HubIntegration>(`/me/integrations/${encodeURIComponent(id)}`).catch((e) => {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }),
    runIntegration: (id) =>
      call<HubIntegration>(`/me/integrations/${encodeURIComponent(id)}/run`, { method: 'POST', body: '{}' }),
    getIntegrationEnablementMap: () => call<Record<string, boolean>>('/me/integrations/enabled'),
    setIntegrationEnabled: (id, enabled) =>
      call<{ integration: HubIntegration | null }>(`/me/integrations/${encodeURIComponent(id)}/enabled`, {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      }).then((r) => r.integration),
    listUtilities: () => call<{ utilities: Utility[] }>('/me/utilities').then((r) => r.utilities),
    getUtilityDetail: (id) => call<CleanupDetailDTO>(`/me/utilities/${encodeURIComponent(id)}`),
    getUtilityConfig: (id) => call<GmailCleanupConfig>(`/me/utilities/${encodeURIComponent(id)}/config`),
    putUtilityConfig: (id, config) =>
      call<GmailCleanupConfig>(`/me/utilities/${encodeURIComponent(id)}/config`, { method: 'PUT', body: JSON.stringify(config) }),
    getUtilityStatus: (id) =>
      call<{ run: UtilityRun | null }>(`/me/utilities/${encodeURIComponent(id)}/status`).then((r) => r.run),
    previewUtility: (id) =>
      call<UtilityRun>(`/me/utilities/${encodeURIComponent(id)}/preview`, { method: 'POST', body: '{}' }),
    runUtility: (id, approveRunId) =>
      call<UtilityRun>(`/me/utilities/${encodeURIComponent(id)}/run`, {
        method: 'POST',
        body: JSON.stringify(approveRunId ? { approveRunId } : {}),
      }),
    beginGmailModify: () => call<OAuthLinkStart>('/accounts/gmail-modify', { method: 'POST', body: '{}' }),
    getFinancialConfig: () => call<FinancialConfig>('/me/financial-config'),
    putFinancialConfig: (config) =>
      call<FinancialConfig>('/me/financial-config', { method: 'PUT', body: JSON.stringify(config) }),
    createPlaidLinkToken: () =>
      call<{ linkToken: string; expiration: string }>('/me/financial/link-token', { method: 'POST', body: '{}' }),
    createHostedPlaidLink: () =>
      call<{ linkToken: string; hostedLinkUrl: string; expiration: string }>('/me/financial/hosted-link-token', { method: 'POST', body: '{}' }),
    createPlaidUpdateLinkToken: (itemId) =>
      call<{ linkToken: string; expiration: string }>(`/me/financial/items/${encodeURIComponent(itemId)}/update-link-token`, { method: 'POST', body: '{}' }),
    createHostedPlaidUpdateLink: (itemId) =>
      call<{ linkToken: string; hostedLinkUrl: string; expiration: string }>(`/me/financial/items/${encodeURIComponent(itemId)}/hosted-update-link-token`, { method: 'POST', body: '{}' }),
    completeHostedPlaid: (linkToken) =>
      call<{ connected: number; duplicates: number; pending: boolean }>('/me/financial/hosted-complete', { method: 'POST', body: JSON.stringify({ linkToken }) }),
    exchangePlaidPublicToken: (publicToken) =>
      call<{ itemId: string; institutionName: string }>('/me/financial/exchange', { method: 'POST', body: JSON.stringify({ publicToken }) }),
    listFinancialItems: () => call<{ items: FinancialItemDTO[] }>('/me/financial/items').then((r) => r.items),
    listFinancialCardholders: () => call<{ cardholders: { raw: string; alias: string }[] }>('/finances/cardholders').then((r) => r.cardholders),
    removeFinancialItem: (itemId) =>
      call<{ ok: boolean }>(`/me/financial/items/${encodeURIComponent(itemId)}`, { method: 'DELETE' }).then(() => undefined),
    purgeFinancial: () => call<{ ok: boolean }>('/me/financial', { method: 'DELETE' }).then(() => undefined),
    getFinancialSummary: () => call<FinancialSummaryDTO>('/finances/summary'),
    getFitnessSummary: () => call<FitnessSummaryResponse>('/fitness/summary'),
    getBodyComposition: (sinceDays) =>
      call<BodyCompositionSeriesDTO>(`/fitness/body-composition${sinceDays ? `?sinceDays=${sinceDays}` : ''}`),
    getWorkoutCompliance: () => call<WorkoutComplianceViewDTO>('/fitness/compliance'),
    syncFitness: () =>
      call<{ bodyComp: number; dexa: number; programs: number; workouts: number }>('/fitness/sync', { method: 'POST', body: '{}' }),
    setFitnessAssistantReads: (enabled) =>
      call<{ assistantReads: boolean }>('/fitness/assistant-reads', { method: 'POST', body: JSON.stringify({ enabled }) }),
    setFitnessWeightUnit: (unit) => call<{ weightUnit: 'lb' | 'kg' }>('/fitness/units', { method: 'POST', body: JSON.stringify({ unit }) }),
    // ── KUDOS — employee thank-you letters ──────────────────────────────────────────────────────
    getChaseConfig: () => call<ChaseConfigDTO>('/chase/config'),
    putChaseConfig: (patch) => call<ChaseConfigDTO>('/chase/config', { method: 'PUT', body: JSON.stringify(patch) }),
    getKudosConfig: () => call<KudosConfigDTO>('/kudos/config'),
    putKudosConfig: (patch) => call<KudosConfigDTO>('/kudos/config', { method: 'PUT', body: JSON.stringify(patch) }),
    getKudosRoster: () => call<{ roster: KudosRosterRowDTO[] }>('/kudos/roster'),
    getKudosSuggestions: () => call<{ suggestions: KudosSuggestionDTO[] }>('/kudos/suggestions'),
    getKudosHistory: (employeeId) => call<{ letters: KudosLetterDTO[] }>(`/kudos/history/${encodeURIComponent(employeeId)}`),
    getKudosRecentLetters: () => call<{ letters: KudosLetterDTO[] }>('/kudos/letters/recent'),
    writeKudosLetter: (body) => call<{ letter: KudosLetterDTO }>('/kudos/letters', { method: 'POST', body: JSON.stringify(body) }),
    snoozeKudosSuggestion: (id, days) =>
      call<{ ok: boolean }>(`/kudos/suggestions/${encodeURIComponent(id)}/snooze`, { method: 'POST', body: JSON.stringify(days ? { days } : {}) }).then(() => undefined),
    dismissKudosSuggestion: (id) =>
      call<{ ok: boolean }>(`/kudos/suggestions/${encodeURIComponent(id)}/dismiss`, { method: 'POST', body: '{}' }).then(() => undefined),
    repullKudos: () => call<{ released: number; setAside: number }>('/kudos/repull', { method: 'POST', body: '{}' }),
    updateKudosPerson: (employeeId, patch) =>
      call<{ ok: boolean }>(`/kudos/roster/${encodeURIComponent(employeeId)}`, { method: 'POST', body: JSON.stringify(patch) }).then(() => undefined),
    draftKudosLetter: (employeeId) =>
      call<{ text: string }>('/kudos/draft', { method: 'POST', body: JSON.stringify({ employeeId }) }),
    syncKudos: () => call<{ employees: number }>('/kudos/sync', { method: 'POST', body: '{}' }),
    setKudosAssistantReads: (enabled) =>
      call<{ assistantReads: boolean }>('/kudos/assistant-reads', { method: 'POST', body: JSON.stringify({ enabled }) }).then(() => undefined),
    linkRippling: (apiKey) => call<unknown>('/accounts/rippling', { method: 'POST', body: JSON.stringify({ apiKey }) }).then(() => undefined),
    getClaveroConfig: () => call<ClaveroConfig>('/me/clavero-config'),
    putClaveroConfig: (config) => call<ClaveroConfig>('/me/clavero-config', { method: 'PUT', body: JSON.stringify(config) }),
    getShopping: () => call<ShoppingSummary>('/me/shopping'),
    refreshShopping: () => call<ShoppingSummary>('/me/shopping/refresh', { method: 'POST', body: '{}' }),
    searchShopping: (query) => call<ShoppingSearchResult>('/me/shopping/search', { method: 'POST', body: JSON.stringify({ query }) }),
    getShoppingProduct: (asin) => call<ProductRead>(`/me/shopping/product/${encodeURIComponent(asin)}`),
    draftShoppingIntent: (lines, allowStashClear) =>
      call<DraftIntentResult>('/me/shopping/intents', {
        method: 'POST',
        body: JSON.stringify(allowStashClear ? { lines, allowStashClear } : { lines }),
      }),
    listShoppingIntents: (opts = {}) => {
      const q = new URLSearchParams();
      if (opts.limit != null) q.set('limit', String(opts.limit));
      if (opts.status) q.set('status', opts.status);
      const qs = q.toString();
      return call<{ intents: LapoOrderIntent[] }>(`/me/shopping/intents${qs ? `?${qs}` : ''}`);
    },
    getShoppingIntent: (id) => call<LapoOrderIntent>(`/me/shopping/intents/${encodeURIComponent(id)}`),
    confirmShoppingIntent: (id, surface) =>
      call<LapoOrderIntent>(`/me/shopping/intents/${encodeURIComponent(id)}/confirm`, { method: 'POST', body: JSON.stringify({ surface }) }),
    cancelShoppingIntent: (id) => call<LapoOrderIntent>(`/me/shopping/intents/${encodeURIComponent(id)}/cancel`, { method: 'POST', body: '{}' }),
    setCartQty: (asin, qty) => call<ShoppingCartSnapshot>(`/me/shopping/cart/${encodeURIComponent(asin)}`, { method: 'PATCH', body: JSON.stringify({ qty }) }),
    removeCartLine: (asin) => call<ShoppingCartSnapshot>(`/me/shopping/cart/${encodeURIComponent(asin)}`, { method: 'DELETE' }),
    clearCart: () => call<ShoppingCartSnapshot>('/me/shopping/cart', { method: 'DELETE' }),
    restoreStash: () => call<DraftIntentResult>('/me/shopping/cart/restore', { method: 'POST', body: '{}' }),
    discardStash: () => call<{ ok: boolean }>('/me/shopping/stash', { method: 'DELETE' }),
    placeCartOrder: () => call<DraftIntentResult>('/me/shopping/cart/checkout', { method: 'POST', body: '{}' }),
    getShoppingOrders: (refresh) => call<{ orders: AmazonOrder[] }>(`/me/shopping/orders${refresh ? '?refresh=true' : ''}`),
    getShoppingBuckets: () => call<AmazonBucketRollup>('/me/shopping/buckets'),
    getShoppingOrder: (orderId) => call<AmazonOrder>(`/me/shopping/orders/${encodeURIComponent(orderId)}`),
    getShoppingMatch: (txnId) => call<AmazonTxnMatch>(`/finances/transactions/${encodeURIComponent(txnId)}/amazon`),
    resolveShoppingMatch: (txnId, input) =>
      call<AmazonTxnMatch>(`/finances/transactions/${encodeURIComponent(txnId)}/amazon/resolve`, { method: 'POST', body: JSON.stringify(input) }),
    setShoppingBucket: (txnId, input) =>
      call<{ updated: number }>(`/finances/transactions/${encodeURIComponent(txnId)}/amazon/bucket`, { method: 'POST', body: JSON.stringify(input) }),
    getFinancialCategories: () => call<{ groups: CategoryGroup[] }>('/finances/categories').then((r) => r.groups),
    listFinancialTransactions: (query = {}) => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) if (v != null) q.set(k, String(v));
      const qs = q.toString();
      return call<{ transactions: FinancialTransaction[] }>(`/finances/transactions${qs ? `?${qs}` : ''}`).then((r) => r.transactions);
    },
    listRecurring: () => call<{ recurring: RecurringStream[] }>('/finances/recurring').then((r) => r.recurring),
    setRecurringOverride: (key, patch) =>
      call<{ key: string }>('/finances/recurring/override', { method: 'POST', body: JSON.stringify({ key, ...patch }) }).then(() => undefined),
    setMerchantName: (merchant, displayName) =>
      call<{ ok: boolean }>('/finances/merchants/rename', { method: 'POST', body: JSON.stringify({ merchant, displayName }) }).then(() => undefined),
    listFinancialRules: () => call<FinancialRulesDTO>('/finances/rules'),
    deleteCategoryRule: (ruleId) =>
      call<{ ok: boolean }>(`/finances/rules/category/${encodeURIComponent(ruleId)}`, { method: 'DELETE' }).then(() => undefined),
    previewFinancialRule: (draft) =>
      call<{ count: number; samples: FinancialTransaction[] }>('/finances/rules/preview', { method: 'POST', body: JSON.stringify(draft) }),
    createFinancialRule: (draft, applyToPast) =>
      call<{ rule: { ruleId: string }; applied: number }>('/finances/rules/category', { method: 'POST', body: JSON.stringify({ ...draft, applyToPast }) }),
    updateFinancialRule: (ruleId, patch, applyToPast) =>
      call<{ rule: { ruleId: string }; applied: number }>(`/finances/rules/category/${encodeURIComponent(ruleId)}`, { method: 'PATCH', body: JSON.stringify({ ...patch, applyToPast }) }),
    putRenameRule: (rule) => call<RenameRuleDTO>('/finances/rules/rename', { method: 'POST', body: JSON.stringify(rule) }),
    deleteRenameRule: (id) =>
      call<{ ok: boolean }>(`/finances/rules/rename/${encodeURIComponent(id)}`, { method: 'DELETE' }).then(() => undefined),
    auditFinancialRules: () => call<RuleAuditDTO>('/finances/rules/audit', { method: 'POST', body: '{}' }),
    justifyTransaction: (txnId, category, applyToPast) =>
      call<{ updated: number; rule: boolean }>(`/finances/transactions/${encodeURIComponent(txnId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ category, applyToPast }),
      }),
    dismissFinancialAlert: (alertId) =>
      call<{ ok: boolean }>(`/finances/alerts/${encodeURIComponent(alertId)}/dismiss`, { method: 'POST', body: '{}' }).then(() => undefined),
    importMonarchCsv: async (csv, fileName) => {
      if (isOffline()) throw new ApiError(0, 'offline');
      const token = await getToken();
      if (!token) throw new ApiError(401, 'not signed in');
      const qs = fileName ? `?name=${encodeURIComponent(fileName)}` : '';
      const res = await guardedFetch(`${base}/me/financial/import/monarch${qs}`, {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'content-type': 'text/csv' },
        body: csv,
      });
      if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
      return (await res.json()) as MonarchImportResultDTO;
    },
    getSubscriptionConfig: () => call<SubscriptionDigestConfig>('/me/subscription-config'),
    putSubscriptionConfig: (config) =>
      call<SubscriptionDigestConfig>('/me/subscription-config', { method: 'PUT', body: JSON.stringify(config) }),
    getSubscriptionCurrent: () => call<SubscriptionIssue | null>('/me/subscription/current'),
    listSubscriptionIssues: () =>
      call<{ issues: SubscriptionIssueSummary[] }>('/me/subscription/issues').then((r) => r.issues),
    getSubscriptionIssue: (id) =>
      call<SubscriptionIssue>(`/me/subscription/issues/${encodeURIComponent(id)}`).catch((e) => {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }),
    addSubscriptionRule: (rule) =>
      call<SubscriptionRule>('/me/subscription/rules', { method: 'POST', body: JSON.stringify(rule) }),
    deleteSubscriptionRule: (id) =>
      call<void>(`/me/subscription/rules/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    listSubscriptionFeedback: () =>
      call<{ entries: SubscriptionFeedbackEntry[] }>('/me/subscription/feedback').then((r) => r.entries),
    setSubscriptionFeedback: (input) =>
      call<{ entries: SubscriptionFeedbackEntry[] }>('/me/subscription/feedback', {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((r) => r.entries),
    uploadBlob: (file) => uploadFile(file),
    signBlob: (uri) => call<SignedUrlDTO>(`/files/sign?uri=${encodeURIComponent(uri)}`),
    generateHeadshot: (input) =>
      call<GenerateHeadshotResult>('/me/integrations/contact-pictures/generate', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    approveHeadshot: (draftId) =>
      call<ApproveHeadshotResult>('/me/integrations/contact-pictures/approve', {
        method: 'POST',
        body: JSON.stringify({ draftId }),
      }),
    runAgenda: (dateKey, timeZone) =>
      call<{ markdown: string | null; eventCount: number }>('/agenda/run', {
        method: 'POST',
        body: JSON.stringify({ dateKey, timeZone }),
      }),
    getContacts: (q) =>
      call<{ contacts: ContactDTO[] }>(`/contacts${q ? `?q=${encodeURIComponent(q)}` : ''}`).then((r) => r.contacts),
    getRelationshipProfile: (entityKey) =>
      call<RelationshipProfileDTO>(`/me/relationship/profiles/${encodeURIComponent(entityKey)}`).catch((err) => {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }),
    confirmIntroducer: (entityKey, name) =>
      call<RelationshipProfileDTO>(`/me/relationship/profiles/${encodeURIComponent(entityKey)}/introducer`, {
        method: 'PUT',
        body: JSON.stringify({ action: 'confirm', name }),
      }),
    dismissIntroducer: (entityKey) =>
      call<RelationshipProfileDTO>(`/me/relationship/profiles/${encodeURIComponent(entityKey)}/introducer`, {
        method: 'PUT',
        body: JSON.stringify({ action: 'dismiss' }),
      }),
    getRecentContacts: () => call<{ contacts: ContactDTO[] }>('/contacts/recent').then((r) => r.contacts),
    mergeContacts: (contactIds) =>
      call<{ canonicalId: string }>('/contacts/merge', { method: 'POST', body: JSON.stringify({ contactIds }) }),
    syncContacts: () => call<{ contacts: number }>('/contacts/sync', { method: 'POST' }),
    getContactSuggestions: () =>
      call<{ suggestions: ContactSuggestionDTO[] }>('/contacts/suggestions').then((r) => r.suggestions),
    confirmContactSuggestion: (suggestionId) =>
      call<{ ok: boolean }>(`/contacts/suggestions/${encodeURIComponent(suggestionId)}/confirm`, { method: 'POST' }).then(() => undefined),
    dismissContactSuggestion: (suggestionId) =>
      call<{ ok: boolean }>(`/contacts/suggestions/${encodeURIComponent(suggestionId)}/dismiss`, { method: 'POST' }).then(() => undefined),
    deleteNote: (noteId) =>
      call<{ deleted: boolean }>(`/notes/${encodeURIComponent(noteId)}`, { method: 'DELETE' }).then(() => undefined),
    openNote: (noteId) =>
      call<{ ok: boolean }>(`/notes/${encodeURIComponent(noteId)}/open`, { method: 'POST' }).then(() => undefined),
    dismissRecent: (noteId) =>
      call<{ ok: boolean }>(`/notes/${encodeURIComponent(noteId)}/recent-dismiss`, { method: 'POST' }).then(() => undefined),
    setStarred: (noteId, starred) =>
      call<{ ok: boolean }>(`/notes/${encodeURIComponent(noteId)}/star`, {
        method: 'POST',
        body: JSON.stringify({ starred }),
      }).then(() => undefined),
    resolvePage: (name) => call<PageResolutionDTO>(`/pages/resolve?name=${encodeURIComponent(name)}`),
    getBacklinks: (noteId, name, opts) => {
      const qs = new URLSearchParams();
      if (name) qs.set('name', name);
      if (opts?.limit != null) qs.set('limit', String(opts.limit));
      if (opts?.offset != null) qs.set('offset', String(opts.offset));
      if (opts?.query) qs.set('q', opts.query);
      const suffix = qs.toString();
      return call<BacklinksDTO>(`/pages/${encodeURIComponent(noteId)}/backlinks${suffix ? `?${suffix}` : ''}`);
    },
    reindexNote: (noteId) =>
      call<{ ok: boolean }>(`/notes/${encodeURIComponent(noteId)}/reindex`, { method: 'POST' }).then(() => undefined),
    getBlock: (blockId) => call<{ block: BlockDTO | null }>(`/blocks/${encodeURIComponent(blockId)}`).then((r) => r.block),
    searchBlocks: (query) =>
      call<{ blocks: BlockDTO[] }>(`/blocks/search?q=${encodeURIComponent(query)}`).then((r) => r.blocks),
    searchContent: (query, signal) =>
      call<{ results: ContentHitDTO[] }>(`/notes/search?q=${encodeURIComponent(query)}`, { signal }).then(
        (r) => r.results,
      ),
    searchPalette: (query, corpora, signal) =>
      call<{ groups: PaletteGroupDTO[] }>(
        `/search/palette?q=${encodeURIComponent(query)}&corpora=${encodeURIComponent(corpora.join(','))}`,
        { signal },
      ).then((r) => r.groups),
    getPageContent: (name) =>
      call<{ content: PageContentDTO | null }>(`/pages/content?name=${encodeURIComponent(name)}`).then((r) => r.content),
    assist: (prompt, dateKey, signal) =>
      call<AssistantResultDTO>('/assistant', { method: 'POST', body: JSON.stringify({ prompt, dateKey }), signal }),
    signalStream: async (onWake, signal) => {
      if (isOffline()) throw new ApiError(0, 'offline');
      const token = await getToken();
      if (!token) throw new ApiError(401, 'not signed in');
      const res = await guardedFetch(`${base}/signals/stream`, {
        headers: { authorization: `Bearer ${token}` },
        signal,
      });
      if (!res.ok || !res.body) throw new ApiError(res.status, await errorMessage(res));
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (value) buffer += decoder.decode(value, { stream: true });
        let cut = buffer.indexOf('\n\n');
        while (cut >= 0) {
          const frame = buffer.slice(0, cut);
          buffer = buffer.slice(cut + 2);
          cut = buffer.indexOf('\n\n');
          if (!/^event: wake$/m.test(frame)) continue;
          const data = /^data: (.+)$/m.exec(frame)?.[1];
          try {
            onWake(data ? (JSON.parse(data) as { dateKey?: string }) : {});
          } catch {
            onWake({});
          }
        }
        if (done) return; // server closed (timeout/redeploy) — caller reconnects
      }
    },
    assistStream: async (prompt, dateKey, onStatus, signal) => {
      if (isOffline()) throw new ApiError(0, 'offline');
      const token = await getToken();
      if (!token) throw new ApiError(401, 'not signed in');
      const res = await guardedFetch(`${base}/assistant/stream`, {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ prompt, dateKey }),
        signal,
      });
      if (!res.ok || !res.body) throw new ApiError(res.status, await errorMessage(res));
      // Minimal SSE reader: frames are `event: <name>\ndata: <json>\n\n`.
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let result: AssistantResultDTO | undefined;
      let streamError: string | undefined;
      const handleFrame = (frame: string): void => {
        const event = /^event: (.+)$/m.exec(frame)?.[1];
        const data = /^data: (.+)$/m.exec(frame)?.[1];
        if (!event || !data) return;
        try {
          const parsed = JSON.parse(data) as Record<string, unknown>;
          if (event === 'status') onStatus(parsed as { type: string; tool?: string; round?: number });
          else if (event === 'result') result = parsed as unknown as AssistantResultDTO;
          else if (event === 'error') streamError = typeof parsed.error === 'string' ? parsed.error : 'assistant failed';
        } catch {
          // skip malformed frames
        }
      };
      for (;;) {
        const { done, value } = await reader.read();
        if (value) buffer += decoder.decode(value, { stream: true });
        let cut = buffer.indexOf('\n\n');
        while (cut >= 0) {
          handleFrame(buffer.slice(0, cut));
          buffer = buffer.slice(cut + 2);
          cut = buffer.indexOf('\n\n');
        }
        if (done) break;
      }
      if (streamError) throw new ApiError(400, streamError);
      if (!result) throw new ApiError(0, 'stream ended without a result');
      return result;
    },
    assistBillImage: async (bytes, contentType, prompt) => {
      if (isOffline()) throw new ApiError(0, 'offline');
      const token = await getToken();
      if (!token) throw new ApiError(401, 'not signed in');
      const qs = typeof prompt === 'string' && prompt.trim() ? `?prompt=${encodeURIComponent(prompt.trim())}` : '';
      const res = await guardedFetch(`${base}/assistant/bill-image${qs}`, {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'content-type': contentType || 'application/octet-stream' },
        body: bytes as BodyInit,
      });
      if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
      return (await res.json()) as AssistantResultDTO;
    },
    confirmBillPay: (billId) => call<{ ok: boolean; status: string }>(`/me/bills/${encodeURIComponent(billId)}/pay`, { method: 'POST' }),
    billStatus: (billId) => call<{ billId: string; status: string; lastError?: string }>(`/me/bills/${encodeURIComponent(billId)}`),
    listBills: () => call<{ bills: BillRecord[] }>('/me/bills').then((r) => r.bills),
    getBill: (billId) => call<BillRecord>(`/me/bills/${encodeURIComponent(billId)}`),
    listJobs: () => call<{ jobs: Job[] }>('/me/jobs').then((r) => r.jobs),
    getJob: (jobId) => call<{ job: Job }>(`/me/jobs/${encodeURIComponent(jobId)}`).then((r) => r.job),
    listWatches: () => call<{ watches: Watch[] }>('/me/watches').then((r) => r.watches),
    createWatch: (input) => call<{ watch: Watch }>('/me/watches', { method: 'POST', body: JSON.stringify(input) }).then((r) => r.watch),
    setWatchEnabled: (id, enabled) => call<{ watch: Watch }>(`/me/watches/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ enabled }) }).then((r) => r.watch),
    deleteWatch: (id) => call<{ ok: boolean }>(`/me/watches/${encodeURIComponent(id)}`, { method: 'DELETE' }).then(() => undefined),
    listBillRuns: (billId) => call<{ runs: BillPayRunDTO[] }>(`/me/bills/${encodeURIComponent(billId)}/runs`).then((r) => r.runs),
    selfPaidBill: (billId) => call<{ ok: boolean; status: string }>(`/me/bills/${encodeURIComponent(billId)}/self-paid`, { method: 'POST' }),
    teachBill: (billId, hint) =>
      call<{ ok: boolean; status: string }>(`/me/bills/${encodeURIComponent(billId)}/teach`, {
        method: 'POST',
        body: JSON.stringify({ hint }),
      }),
    cancelAutoPayBill: (billId) => call<{ ok: boolean; status: string }>(`/me/bills/${encodeURIComponent(billId)}/cancel-auto`, { method: 'POST' }),
    cancelRetryBill: (billId) => call<{ ok: boolean; status: string }>(`/me/bills/${encodeURIComponent(billId)}/cancel-retry`, { method: 'POST' }),
    getBillKnowledge: (billId) =>
      call<{ dossier: BillerDossier | null; overlay: BillerOverlay | null }>(`/me/bills/${encodeURIComponent(billId)}/knowledge`),
    setBillArchived: (billId, archived) =>
      call<{ ok: boolean; archived: boolean }>(`/me/bills/${encodeURIComponent(billId)}/${archived ? 'archive' : 'unarchive'}`, { method: 'POST' }),
    deleteBill: (billId) => call<{ ok: boolean }>(`/me/bills/${encodeURIComponent(billId)}`, { method: 'DELETE' }),
    importBillPhoto: async (bytes, contentType) => {
      if (isOffline()) throw new ApiError(0, 'offline');
      const token = await getToken();
      if (!token) throw new ApiError(401, 'not signed in');
      const res = await guardedFetch(`${base}/me/bills/import`, {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'content-type': contentType || 'application/octet-stream' },
        body: bytes as BodyInit,
      });
      if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
      return (await res.json()) as { isBill: boolean; billId?: string };
    },
    assistThread: () => call<{ turns: AssistantTurnDTO[] }>('/assistant/thread').then((r) => r.turns),
    assistClear: () => call<{ ok: boolean }>('/assistant/clear', { method: 'POST' }).then(() => undefined),
    voiceCallStatus: (callId) => call<VoiceCallStatusDTO>(`/integrations/voice/calls/${encodeURIComponent(callId)}`),
    getSignals: (dateKey, today) =>
      call<{ signals: SignalDTO[] }>(
        `/signals?dateKey=${encodeURIComponent(dateKey)}&today=${encodeURIComponent(today ?? dateKey)}`,
      ).then((r) => r.signals),
    getSignalsRange: (from, to) =>
      call<{ signals: SignalDTO[] }>(`/signals?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`).then(
        (r) => r.signals,
      ),
    markSignalSeen: (signalId) =>
      call<{ signal: SignalDTO }>(`/signals/${encodeURIComponent(signalId)}/seen`, { method: 'POST' }).then((r) => r.signal),
    dismissSignal: (signalId) =>
      call<{ signal: SignalDTO }>(`/signals/${encodeURIComponent(signalId)}/dismiss`, { method: 'POST' }).then((r) => r.signal),
    setAsideSignal: (signalId) =>
      call<{ signal: SignalDTO }>(`/signals/${encodeURIComponent(signalId)}/set-aside`, { method: 'POST' }).then((r) => r.signal),
    createSignalArtifact: (signalId, kind, opts) =>
      call<SignalCreateArtifactResultDTO>(`/signals/${encodeURIComponent(signalId)}/create`, {
        method: 'POST',
        body: JSON.stringify({ kind, number: opts?.number }),
      }),
    resolveCallNumber: (signalId) =>
      call<{ targetName: string; candidates: Array<{ number: string; source: 'contacts' | 'maps' | 'web'; label: string }> }>(
        `/signals/${encodeURIComponent(signalId)}/call/resolve`,
        { method: 'POST' },
      ),
    removeSignalArtifact: (signalId, artifactId) =>
      call<{ signal: SignalDTO }>(
        `/signals/${encodeURIComponent(signalId)}/artifacts/${encodeURIComponent(artifactId)}/remove`,
        { method: 'POST' },
      ).then((r) => r.signal),
    snoozeSignal: (signalId, days) =>
      call<{ signal: SignalDTO }>(`/signals/${encodeURIComponent(signalId)}/snooze`, {
        method: 'POST',
        body: JSON.stringify({ days }),
      }).then((r) => r.signal),
    actSignal: (signalId, opts) =>
      call<SignalActResultDTO>(`/signals/${encodeURIComponent(signalId)}/act`, {
        method: 'POST',
        body: JSON.stringify({ editedBody: opts?.editedBody, actionIndex: opts?.actionIndex }),
      }),
    rejectSignal: (signalId) =>
      call<{ signal: SignalDTO }>(`/signals/${encodeURIComponent(signalId)}/reject`, { method: 'POST' }).then((r) => r.signal),
    markSpamSignal: (signalId) =>
      call<SignalActResultDTO>(`/signals/${encodeURIComponent(signalId)}/spam`, { method: 'POST' }),
    markAutomatedSignal: (signalId) =>
      call<{ signal: SignalDTO }>(`/signals/${encodeURIComponent(signalId)}/automated`, { method: 'POST' }).then((r) => r.signal),
    draftReply: (signalId, opts) =>
      call<{ queued: boolean }>(`/signals/${encodeURIComponent(signalId)}/draft-reply`, {
        method: 'POST',
        body: JSON.stringify(opts),
      }).then(() => undefined),
    getSignalSourceEmail: (signalId) =>
      call<{ email: SignalSourceEmailDTO | null }>(`/signals/${encodeURIComponent(signalId)}/source-email`)
        .then((r) => r.email)
        .catch(() => null),
    regenerateDraft: (signalId, opts) =>
      call<{ draftBody: string }>(`/signals/${encodeURIComponent(signalId)}/regenerate-draft`, {
        method: 'POST',
        body: JSON.stringify(opts),
      }).catch(() => null),
    sendSignalReply: (signalId, opts) =>
      call<{ ok?: boolean; relinkRequired?: string; archiveRelink?: string }>(`/signals/${encodeURIComponent(signalId)}/send-reply`, {
        method: 'POST',
        body: JSON.stringify(opts),
      }).catch(() => null),
    resetTriage: () => call<{ cleared: number }>('/signals/triage/reset', { method: 'POST' }).then((r) => r.cleared),
    reconcileSignals: () => call<{ checked: number; cleared: number }>('/signals/reconcile', { method: 'POST' }),
    resurfaceNotRelevant: (targetId) =>
      call<{ ok: boolean }>('/resurface/not-relevant', { method: 'POST', body: JSON.stringify({ targetId }) }).then(() => undefined),
    getResurfaceConfig: () => call<{ enabled: boolean }>('/resurface/config'),
    setResurfaceConfig: (enabled) =>
      call<{ enabled: boolean }>('/resurface/config', { method: 'POST', body: JSON.stringify({ enabled }) }).then(() => undefined),
    runResurface: () =>
      call<{ candidates: number; items: number; emitted: boolean; skipped: boolean }>('/me/resurface/run', { method: 'POST' }),
    listParkedIngest: () => call<{ parked: ParkedIngest[] }>('/ingest-triage/parked').then((r) => r.parked),
    restoreParkedIngest: (parkedId) =>
      call<{ restored: boolean }>(`/ingest-triage/parked/${encodeURIComponent(parkedId)}/restore`, { method: 'POST' })
        .then((r) => r.restored)
        .catch(() => false),
    getIngestTriageScorecard: () => call<{ parks: number; restores: number; precision: number }>('/ingest-triage/scorecard'),
    recordSignalFeedback: (signalId, input) =>
      call<{ ok: boolean }>(`/signals/${encodeURIComponent(signalId)}/feedback`, { method: 'POST', body: JSON.stringify(input) })
        .then((r) => r.ok)
        .catch(() => false),
    // HELM P1-8 — the morning brief + its thumb-reaction sink (C9).
    getBrief: (dateKey) =>
      call<{ brief: MorningBriefDTO | null }>(`/me/attention/brief/${encodeURIComponent(dateKey)}`)
        .then((r) => r.brief)
        .catch(() => null),
    recordBriefReaction: (deliveryId, reaction, wrongCause) =>
      call<{ ok: boolean }>('/me/attention/brief/react', {
        method: 'POST',
        body: JSON.stringify({ deliveryId, reaction, ...(wrongCause ? { wrongCause } : {}) }),
      })
        .then((r) => r.ok)
        .catch(() => false),
    teach: (input) =>
      call<{ ok: boolean }>('/teach', { method: 'POST', body: JSON.stringify(input) })
        .then((r) => r.ok)
        .catch(() => false),
    recordAutonomyFeedback: (eventId, quality, note) =>
      call<{ ok: boolean }>(`/feedback/autonomy/${encodeURIComponent(eventId)}`, { method: 'POST', body: JSON.stringify({ quality, note }) })
        .then((r) => r.ok)
        .catch(() => false),
    getFeedbackScores: () => call<{ scores: KindUsefulness[] }>('/feedback/scores').then((r) => r.scores),
    listGuidance: () => call<{ entries: GuidanceEntry[] }>('/guidance').then((r) => r.entries),
    putGuidance: (input) => call<{ entry: GuidanceEntry }>('/guidance', { method: 'PUT', body: JSON.stringify(input) }).then((r) => r.entry),
    deleteGuidance: (entryId) => call<{ ok: boolean }>(`/guidance?entryId=${encodeURIComponent(entryId)}`, { method: 'DELETE' }).then(() => undefined),
    approveGuidance: (entryId, text) =>
      call<{ ok: boolean }>('/guidance/approve', {
        method: 'POST',
        body: JSON.stringify({ entryId, ...(text && text.trim() ? { text: text.trim() } : {}) }),
      }).then(() => undefined),
    rejectGuidance: (entryId) =>
      call<{ ok: boolean }>('/guidance/reject', { method: 'POST', body: JSON.stringify({ entryId }) }).then(() => undefined),
    getAttentionLedger: (days) =>
      call<{ days: number; deliveries: DeliveryRecord[] }>(`/me/attention/ledger?days=${days ?? 7}`).then((r) => r.deliveries),
    listAssistantMemories: () => call<{ memories: AssistantMemoryDTO[] }>('/me/assistant/memory').then((r) => r.memories),
    createAssistantMemory: (input) =>
      call<{ memory: AssistantMemoryDTO }>('/me/assistant/memory', { method: 'POST', body: JSON.stringify(input) }).then((r) => r.memory),
    updateAssistantMemory: (memoryId, patch) =>
      call<{ memory: AssistantMemoryDTO }>(`/me/assistant/memory/${encodeURIComponent(memoryId)}`, {
        method: 'PUT',
        body: JSON.stringify(patch),
      }).then((r) => r.memory),
    deleteAssistantMemory: (memoryId) =>
      call<{ ok: boolean }>(`/me/assistant/memory/${encodeURIComponent(memoryId)}`, { method: 'DELETE' }).then(() => undefined),
    // HELM P3-6 — the two panes' read/edit surface.
    listPinnedMemory: () => call<{ memories: AssistantMemoryDTO[] }>('/me/pinned-memory').then((r) => r.memories),
    listRules: () => call<{ rules: RuleDTO[] }>('/me/rules').then((r) => r.rules),
    editRule: (ruleId, text) =>
      call<{ rule: RuleDTO }>(`/me/rules/${encodeURIComponent(ruleId)}/edit`, { method: 'POST', body: JSON.stringify({ text }) }).then((r) => r.rule),
    retireRule: (ruleId) =>
      call<{ rule: RuleDTO }>(`/me/rules/${encodeURIComponent(ruleId)}/retire`, { method: 'POST' }).then(() => undefined),
    explain: (ref) => call<ExplainResultDTO>(`/me/explain?ref=${encodeURIComponent(ref)}`),
    getSignalMetrics: () => call<{ metrics?: SignalMetrics[] } | SignalMetrics[]>('/signals/metrics').then((r) => (Array.isArray(r) ? r : (r.metrics ?? []))),
    getDispositionPolicy: () =>
      call<{ entries: DispositionPolicyEntryDTO[] }>('/me/disposition-policy').then((r) => r.entries),
    setDispositionPolicy: (category, policy) =>
      call<{ ok: boolean }>('/me/disposition-policy', { method: 'PUT', body: JSON.stringify({ category, policy }) }).then(() => undefined),
    removeDispositionPolicy: (category) =>
      call<{ ok: boolean }>(`/me/disposition-policy?category=${encodeURIComponent(category)}`, { method: 'DELETE' }).then(() => undefined),
    getDispositionProposals: () =>
      call<{ proposals: DispositionProposalDTO[] }>('/me/disposition-proposals').then((r) => r.proposals),
    acceptDispositionProposal: (category) =>
      call<{ ok: boolean }>('/me/disposition-proposals/accept', { method: 'POST', body: JSON.stringify({ category }) }).then(() => undefined),
    getDispositionFeedback: () =>
      call<{ learned: DispositionLearnedDTO[] }>('/me/disposition-feedback').then((r) => r.learned),
    clearDispositionFeedback: (key) =>
      call<{ ok: boolean }>(`/me/disposition-feedback${key ? `?key=${encodeURIComponent(key)}` : ''}`, { method: 'DELETE' }).then(() => undefined),
    // ── AXIS — autonomy (trust/routing rules, proposals, recap, scorecard, undo) ─────────────────
    getAutonomyStatus: () => call<{ enabled: boolean; rules: number }>('/me/autonomy/status'),
    runColdStart: () =>
      call<{ proposals: string[]; fewShotSeeds: number; scannedMessages: number }>('/me/autonomy/cold-start', { method: 'POST' }),
    listTrustRules: () => call<{ rules: TrustRuleDTO[] }>('/me/autonomy/rules').then((r) => r.rules),
    setTrustRule: (ruleKey, policy, target) =>
      call<{ rule: TrustRuleDTO }>('/me/autonomy/rules', {
        method: 'PUT',
        body: JSON.stringify({ ruleKey, policy, ...(target ? { target } : {}) }),
      }).then((r) => r.rule),
    removeTrustRule: (ruleKey) =>
      call<{ ok: boolean }>(`/me/autonomy/rules?ruleKey=${encodeURIComponent(ruleKey)}`, { method: 'DELETE' }).then(() => undefined),
    resetTrustRule: (ruleKey) =>
      call<{ ok: boolean }>('/me/autonomy/rules/reset', { method: 'POST', body: JSON.stringify({ ruleKey }) }).then(() => undefined),
    acceptGraduation: (ruleKey, scope) =>
      call<{ rule: TrustRuleDTO }>('/me/autonomy/proposals/accept', {
        method: 'POST',
        body: JSON.stringify({ ruleKey, ...(scope ? { scope } : {}) }),
      }).then(() => undefined),
    declineGraduation: (ruleKey) =>
      call<{ ok: boolean }>('/me/autonomy/proposals/decline', { method: 'POST', body: JSON.stringify({ ruleKey }) }).then(() => undefined),
    // ── HELM P4-3 — scoped grants ────────────────────────────────────────────────────────────────
    listGrants: () => call<{ grants: GrantDTO[] }>('/me/grants').then((r) => r.grants),
    createGrant: (input) =>
      call<{ grant: GrantDTO }>('/me/grants', { method: 'POST', body: JSON.stringify(input) }).then((r) => r.grant),
    revokeGrant: (grantId) =>
      call<{ ok: boolean }>(`/me/grants/${encodeURIComponent(grantId)}`, { method: 'DELETE' }).then(() => undefined),
    listRoutingRules: () => call<{ rules: RoutingRuleDTO[] }>('/me/autonomy/routing').then((r) => r.rules),
    setRoutingRule: (rule) =>
      call<{ rule: RoutingRuleDTO }>('/me/autonomy/routing', { method: 'PUT', body: JSON.stringify(rule) }).then((r) => r.rule),
    removeRoutingRule: (ruleKey) =>
      call<{ ok: boolean }>(`/me/autonomy/routing?ruleKey=${encodeURIComponent(ruleKey)}`, { method: 'DELETE' }).then(() => undefined),
    getAutonomyRecap: (dateKey) =>
      call<{ events: AutonomyEventDTO[] }>(`/me/autonomy/recap${dateKey ? `?dateKey=${encodeURIComponent(dateKey)}` : ''}`).then((r) => r.events),
    getAutonomyScorecard: (from, to) => {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const qs = params.toString();
      return call<{ days: AutonomyScorecardDayDTO[] }>(`/me/autonomy/scorecard${qs ? `?${qs}` : ''}`).then((r) => r.days);
    },
    undoAutonomyEvent: (eventId) =>
      call<{ ok?: boolean }>('/me/autonomy/undo', { method: 'POST', body: JSON.stringify({ eventId }) })
        .then(() => true)
        .catch(() => false),
    undoAutonomyDay: (dateKey) =>
      call<{ undone: number; failed: number }>('/me/autonomy/undo', { method: 'POST', body: JSON.stringify({ dateKey }) }),
    getShipFrom: () => call<{ address: ShipAddressDTO }>('/shipping/ship-from').then((r) => r.address),
    putShipFrom: (address) =>
      call<{ address: ShipAddressDTO }>('/shipping/ship-from', { method: 'PUT', body: JSON.stringify({ address }) }).then(
        (r) => r.address,
      ),
    draftShipment: (prompt) =>
      call<DraftResultDTO>('/shipping/draft', { method: 'POST', body: JSON.stringify({ prompt }) }),
    buyLabel: (easypostId, rateId) =>
      call<{ shipment: ShipmentDTO }>('/shipping/buy', {
        method: 'POST',
        body: JSON.stringify({ easypostId, rateId }),
      }).then((r) => r.shipment),
    listShipments: () => call<{ shipments: ShipmentDTO[] }>('/shipping/labels').then((r) => r.shipments),
    backfillShipments: (limit) =>
      call<{ imported: number; shipments: ShipmentDTO[] }>('/shipping/backfill', {
        method: 'POST',
        body: JSON.stringify({ limit }),
      }),
    syncShipments: (limit) =>
      call<{ imported: number; refreshed: number; shipments: ShipmentDTO[] }>('/shipping/sync', {
        method: 'POST',
        body: JSON.stringify({ limit }),
      }),
    refreshShipment: (shipmentId) =>
      call<{ shipment: ShipmentDTO }>(`/shipping/labels/${encodeURIComponent(shipmentId)}/refresh`, {
        method: 'POST',
      }).then((r) => r.shipment),
    updateShipmentDetails: (shipmentId, patch) =>
      call<{ shipment: ShipmentDTO }>(`/shipping/labels/${encodeURIComponent(shipmentId)}/details`, {
        method: 'POST',
        body: JSON.stringify(patch),
      }).then((r) => r.shipment),
    archiveShipment: (shipmentId, archived) =>
      call<{ shipment: ShipmentDTO }>(`/shipping/labels/${encodeURIComponent(shipmentId)}/archive`, {
        method: 'POST',
        body: JSON.stringify({ archived }),
      }).then((r) => r.shipment),
    trackPackage: (trackingCode, carrier, label) =>
      call<{ tracked: TrackedPackageDTO }>('/shipping/track', {
        method: 'POST',
        body: JSON.stringify({ trackingCode, carrier, label }),
      }).then((r) => r.tracked),
    listTracked: () => call<{ tracked: TrackedPackageDTO[] }>('/shipping/tracked').then((r) => r.tracked),
    scanEmailForShipments: () =>
      call<{ mailboxes: number; scanned: number; tracked: number }>('/shipping/scan-email', { method: 'POST' }),
    refreshTracked: (trackedId) =>
      call<{ tracked: TrackedPackageDTO }>(`/shipping/tracked/${encodeURIComponent(trackedId)}/refresh`, {
        method: 'POST',
      }).then((r) => r.tracked),
    updateTrackedDetails: (trackedId, patch) =>
      call<{ tracked: TrackedPackageDTO }>(`/shipping/tracked/${encodeURIComponent(trackedId)}/details`, {
        method: 'POST',
        body: JSON.stringify(patch),
      }).then((r) => r.tracked),
    archiveTracked: (trackedId, archived) =>
      call<{ tracked: TrackedPackageDTO }>(`/shipping/tracked/${encodeURIComponent(trackedId)}/archive`, {
        method: 'POST',
        body: JSON.stringify({ archived }),
      }).then((r) => r.tracked),
    untrack: (trackedId) =>
      call<{ ok: boolean }>(`/shipping/tracked/${encodeURIComponent(trackedId)}/remove`, {
        method: 'POST',
      }).then(() => undefined),
    getOAuthConsent: (clientId, redirectUri, scope) =>
      call<OAuthConsentInfo>(
        `/oauth/consent?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(
          redirectUri,
        )}&scope=${encodeURIComponent(scope)}`,
      ),
    approveOAuth: (input) =>
      call<{ redirect: string; scopes: string[] }>('/oauth/authorize', {
        method: 'POST',
        body: JSON.stringify({
          client_id: input.clientId,
          redirect_uri: input.redirectUri,
          scope: input.scope,
          state: input.state,
          code_challenge: input.codeChallenge,
          code_challenge_method: input.codeChallengeMethod,
        }),
      }),
    listAuthorizedApps: () =>
      call<{ apps: AuthorizedAppDTO[] }>('/me/authorized-apps').then((r) => r.apps),
    revokeAuthorizedApp: (grantId) =>
      call<{ ok: boolean }>('/me/authorized-apps/revoke', {
        method: 'POST',
        body: JSON.stringify({ grantId }),
      }).then(() => undefined),

    // --- Decision Ledger (LEDGER-06) ---
    getDecisions: (query = {}) => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) if (v != null && v !== '') p.set(k, String(v));
      const qs = p.toString();
      return call<{ decisions: DecisionRecord[] }>(`/decisions${qs ? `?${qs}` : ''}`).then((r) => r.decisions);
    },
    getDecision: (decisionId) => call<DecisionDetailDTO>(`/decisions/${encodeURIComponent(decisionId)}`),
    createDecision: (input) => call<DecisionRecord>('/decisions', { method: 'POST', body: JSON.stringify(input) }),
    patchDecision: (decisionId, fields) =>
      call<DecisionRecord>(`/decisions/${encodeURIComponent(decisionId)}`, { method: 'PATCH', body: JSON.stringify(fields) }),
    deleteDecision: (decisionId, reason, correctionReason) => {
      const qs = new URLSearchParams();
      if (reason) qs.set('reason', reason);
      if (correctionReason) qs.set('correctionReason', correctionReason);
      const q = qs.toString();
      return call<{ ok: boolean }>(`/decisions/${encodeURIComponent(decisionId)}${q ? `?${q}` : ''}`, { method: 'DELETE' }).then(
        () => undefined,
      );
    },
    mergeDecisions: (keepId, mergeIds) =>
      call<DecisionRecord>('/decisions/merge', { method: 'POST', body: JSON.stringify({ keepId, mergeIds }) }),
    setDecisionStatus: (decisionId, status, supersedesId) =>
      call<DecisionRecord>(`/decisions/${encodeURIComponent(decisionId)}/status`, { method: 'POST', body: JSON.stringify({ status, supersedesId }) }),
    getDecisionRules: () => call<{ rules: DecisionRule[] }>('/decisions/rules').then((r) => r.rules),
    upsertDecisionRule: (rule) => call<DecisionRule>('/decisions/rules', { method: 'POST', body: JSON.stringify(rule) }),
    setDecisionRuleEnabled: (ruleId, enabled) =>
      call<DecisionRule>(`/decisions/rules/${encodeURIComponent(ruleId)}`, { method: 'PATCH', body: JSON.stringify({ enabled }) }),
    deleteDecisionRule: (ruleId) =>
      call<{ ok: boolean }>(`/decisions/rules/${encodeURIComponent(ruleId)}`, { method: 'DELETE' }).then(() => undefined),
    getDecisionLedgerConfig: () => call<DecisionLedgerConfig>('/me/decision-ledger-config'),
    putDecisionLedgerConfig: (patch) =>
      call<DecisionLedgerConfig>('/me/decision-ledger-config', { method: 'PUT', body: JSON.stringify(patch) }),
    runDecisionLedger: () =>
      call<{ created: number; merged: number; superseded: number }>('/me/decisions/run', { method: 'POST', body: '{}' }),
    backfillDecisionLedger: (days) =>
      call<{ processed: number; created: number }>('/me/decisions/backfill', { method: 'POST', body: JSON.stringify({ days }) }),

    noteForEvent: (eventId) =>
      call<{ noteId: string | null }>(`/notes/by-event?eventId=${encodeURIComponent(eventId)}`).then((r) => r.noteId),
    bindNoteToEvent: (noteId, eventId) =>
      call<{ ok: boolean }>(`/notes/${encodeURIComponent(noteId)}/bind-event`, {
        method: 'POST',
        body: JSON.stringify({ eventId }),
      }).then(() => undefined),

    // --- LOOM (initiative×quarter matrix) ---
    loom: {
      board: (opts) => {
        const qs = new URLSearchParams();
        if (opts?.all) qs.set('all', '1');
        if (opts?.from) qs.set('from', opts.from);
        if (opts?.to) qs.set('to', opts.to);
        const suffix = qs.toString();
        return call<LoomBoardDTO>(`/loom/board${suffix ? `?${suffix}` : ''}`);
      },
      pick: (q, limit) =>
        call<{ results: LoomPickResultDTO[] }>(`/loom/pick?q=${encodeURIComponent(q)}${limit ? `&limit=${limit}` : ''}`).then(
          (r) => r.results,
        ),
      createInitiative: (input) =>
        call<LoomInitiativeDTO>('/loom/initiatives', { method: 'POST', body: JSON.stringify(input) }),
      updateInitiative: (id, patch) =>
        call<LoomInitiativeDTO>(`/loom/initiatives/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
      deleteInitiative: (id) =>
        call<{ deleted: boolean }>(`/loom/initiatives/${encodeURIComponent(id)}`, { method: 'DELETE' }).then(() => undefined),
      reorderInitiatives: (orderedIds) =>
        call<{ ok: boolean }>('/loom/initiatives/order', { method: 'PUT', body: JSON.stringify({ orderedIds }) }).then(
          () => undefined,
        ),
      upsertPlacement: (input) => call<LoomPlacementDTO>('/loom/placements', { method: 'PUT', body: JSON.stringify(input) }),
      removePlacement: (itemKey) =>
        call<{ removed: LoomPlacementDTO }>(`/loom/placements?itemKey=${encodeURIComponent(itemKey)}`, { method: 'DELETE' })
          .then((r) => r.removed)
          .catch(() => null),
      createItem: (input) =>
        call<{ item: LoomItemDTO; placement?: LoomPlacementDTO }>('/loom/items', { method: 'POST', body: JSON.stringify(input) }),
      createThread: (input) => call<LoomThreadDTO>('/loom/threads', { method: 'POST', body: JSON.stringify(input) }),
      updateThread: (id, patch) =>
        call<LoomThreadDTO>(`/loom/threads/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
      deleteThread: (id) =>
        call<{ deleted: boolean }>(`/loom/threads/${encodeURIComponent(id)}`, { method: 'DELETE' }).then(() => undefined),
      createLink: (aKey, bKey) => call<LoomLinkDTO>('/loom/links', { method: 'PUT', body: JSON.stringify({ aKey, bKey }) }),
      deleteLink: (id) =>
        call<{ deleted: boolean }>(`/loom/links/${encodeURIComponent(id)}`, { method: 'DELETE' }).then(() => undefined),
      classify: (noteId) =>
        call<{ placed: number; suggested: number }>('/loom/classify', { method: 'POST', body: JSON.stringify({ noteId }) }),
      // --- TRELLIS ---
      initiativeDetail: (idOrName) =>
        call<LoomInitiativeDetailDTO>(`/loom/initiatives/${encodeURIComponent(idOrName)}`),
      patchInitiative: (id, patch) =>
        call<LoomInitiativeDTO>(`/loom/initiatives/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
      putBrief: (id, brief) =>
        call<LoomInitiativeDTO>(`/loom/initiatives/${encodeURIComponent(id)}/brief`, { method: 'PUT', body: JSON.stringify(brief) }),
      draftCycle: (id) =>
        call<{ opened: number; drafted: number }>(`/loom/initiatives/${encodeURIComponent(id)}/draft-cycle`, { method: 'POST', body: '{}' }),
      proposals: (state = 'pending') =>
        call<{ proposals: LoomProposalDTO[] }>(`/loom/proposals?state=${encodeURIComponent(state)}`).then((r) => r.proposals),
      acceptProposal: (id) =>
        call<{ ok: boolean; initiativeId?: string; initiativeName?: string }>(
          `/loom/proposals/${encodeURIComponent(id)}/accept`,
          { method: 'POST', body: '{}' },
        ),
      rejectProposal: (id) =>
        call<{ rejected: boolean }>(`/loom/proposals/${encodeURIComponent(id)}/reject`, { method: 'POST', body: '{}' }),
    },
  };
}
